import { 
  getOrderById, 
  saveOrder, 
  saveImage, 
  getImagesByOrderAndStep, 
  deleteImage, 
  getClientOptionsAll, 
  getCatalogueChildren, 
  loadPacksOffline, 
  loadIncompatibilitiesOffline
} from '../services/db';
//import { syncOrders } from '../services/sync';
import { ImageCapture } from '../components/image-capture';
//import { DrawingCanvas } from '../components/drawing-canvas';
import { SignaturePadComponent } from '../components/signature-pad';
//import { fetchProductsFromAPI } from '../services/api';
import type { Customer } from '../models/customer';
import type { propertyDetails } from '../models/propertyDetail';
import type { Order } from '../models/order';
import type { StepData } from '../models/stepData';
import type { Wizard1Customer } from '../models/Wizard1Customer';
import type { Wizard2Property } from '../models/wizard2Property';
import type { Wizard3Products } from '../models/wizard3Products';
import type { Wizard4Decorations } from '../models/wizard4Decorations';
import type { Wizard5Drawings } from '../models/wizard5Drawings';
import type { Wizard6Signoff } from '../models/wizard6Signoff';
import { getDefaultAddress, type Address } from '../models/address';
//import { get } from 'idb-keyval';
import type { Object2d } from '../models/drawingData';
import { 
  stepTitles, 
  ORDER_STATUS_SUBMITTED, 
  ORDER_STATUS_APPROVED, 
  ORDER_STATUS_REJECTED, 
  SURVEYOR_ROLE, 
  SALESREP_ROLE, 
  WIZARD_BUTTON_BACK_TEXT, 
  WIZARD_BUTTON_NEXT_TEXT, 
  WIZARD_BUTTON_SAVE_AND_CLOSE_TEXT, 
  WIZARD_BUTTON_PREV_TEXT 
} from '../constants';
import { getUserRole } from '../services/auth';
import { showConfirmationModal } from '../components/confirmation-modal';
import { navigate } from '../routing/router';
import type { PackSelectionContext } from '../models/packSelectionContext';
import type { PackCategory, ProductPackCache } from '../models/productPackCache';
import { formatPackLabel } from '../utils';

let currentStep = 0;
let orderId: number;
let orderData: Order;
let signaturePadInstance: SignaturePadComponent | null = null;



const gridSize: number = 20; // 50px per meter
//const objectDefaultWidth = 20;
//const objectDefaultHeight = 30;
const handleSize = 12;

//Create a vcariable to hold the selected object type which will be an element on the canvas.
let selectedObjectType : string = 'door'; 

// Holds the fetched SVG content and its asset path for the currently selected bath product
let selectedProductSvgContent: string | null = null;
let selectedProductSvgSrc: string | null = null;

// Type definition for client option items
type ClientOptionItems = Array<{ field_key: string; field_value: string; field_title: string | null; price: number | null }>;

type CompatibilityLookup = {
  optionVariant: Set<string>;
  optionOption: Set<string>;
};

// Store cached client options globally to avoid multiple DB calls
let cachedClientOptions: ClientOptionItems | null = null;
let packsByRange: ProductPackCache[] = [];
let incompatibilityLookups: CompatibilityLookup | null = null;
// Type definition for selected option with price
/*type SelectedOptionWithPrice = {
  fieldKey: string;
  fieldLabel: string;
  selectedText: string;
  price: number;
};*/

// Type definition for stored price data
type StoredPriceItem = {
  fieldKey: string;
  fieldLabel: string;
  selectedValue: string;
  selectedText: string;
  price: number;
};

// Initialize canvas

//Hold the mode which can be move, resize, rotate
let mode = "move"; // Default mode

// Global storage for selected prices (persists across step changes)
let storedPrices: Map<string, StoredPriceItem> = new Map();

// Initialize photos and drawings section
// Store image metadata (id from database)
let photoSlots: Array<{ id: number; blobUrl: string; tag: string } | null> = [null, null, null, null, null, null];

// Enable dragging for objects - IMPROVED VERSION
let dragState: { 
  isDragging: boolean; 
  startX: number; 
  startY: number; 
  initialX: number;
  initialY: number;
  element: SVGGElement | null 
} = {
  isDragging: false,
  startX: 0,
  startY: 0,
  initialX: 0,
  initialY: 0,
  element: null
};

/**
 * Capture prices from current DOM and store them in the global storedPrices Map
 * This should be called when leaving a step to preserve price data
 */
function capturePricesFromCurrentStep() {
  //console.log('[capturePricesFromCurrentStep] Capturing prices from current step');
  
  // Find all select elements with data-orderoptions attribute in current DOM
  const selectElements = document.querySelectorAll('select[data-orderoptions]');
  
  selectElements.forEach((selectElement) => {
    const select = selectElement as HTMLSelectElement;
    const fieldKey = select.getAttribute('data-orderoptions') || '';
    
    // Skip if nothing is selected or default option selected
    if (!select.value || select.value === '') {
      // Remove from stored prices if nothing selected
      storedPrices.delete(fieldKey);
      return;
    }
    
    // Get the selected option element
    const selectedOption = select.options[select.selectedIndex];
    if (!selectedOption) {
      return;
    }
    
    // Check if this option has a price
    const priceAttr = selectedOption.getAttribute('data-price');
    const price = priceAttr && priceAttr !== 'null' ? parseFloat(priceAttr) : 0;
    
    // Get a friendly label for this field
    const fieldLabel = select.previousElementSibling?.textContent || 
                       select.getAttribute('name') || 
                       fieldKey;
    
    // Store this selection (even if price is 0, so we can track selections)
    storedPrices.set(fieldKey, {
      fieldKey,
      fieldLabel: fieldLabel.replace(':', '').trim(),
      selectedValue: select.value,
      selectedText: selectedOption.textContent || selectedOption.value,
      price: price
    });
    
    //console.log(`[capturePricesFromCurrentStep] Stored ${fieldKey}: ${selectedOption.textContent} = £${price}`);
  });
  
  //console.log(`[capturePricesFromCurrentStep] Total stored items: ${storedPrices.size}`);
}

/**
 * Generate formatted order summary text with line items and total from stored prices
 * This uses the storedPrices Map which persists across step changes
 * @returns Formatted summary string
 */
function generateOrderSummary(): { summary: string; total: number } {
  console.log('[generateOrderSummary] Generating from stored prices, count:', storedPrices.size);
  
  // Filter to only items with prices > 0
  const itemsWithPrices = Array.from(storedPrices.values()).filter(item => item.price > 0);
  
  // Collect list-based items with prices
  const listBasedItems: Array<{fieldLabel: string; selectedText: string; price: number}> = [];
  
  // Process Step 2 (Property) list-based items
  const step2PropertyData = orderData?.step_data?.steps?.step2?.data?.propertyData;
  if (step2PropertyData) {
    // 1. Equipment to Install (string[])
    if (step2PropertyData.equipmentToBeInstalled && step2PropertyData.equipmentToBeInstalled.length > 0) {
      step2PropertyData.equipmentToBeInstalled.forEach((equipmentText) => {
        const option = cachedClientOptions?.find(
          item => item.field_key === 'equipment_installation' && item.field_value === equipmentText
        );
        if (option?.price && option.price > 0) {
          listBasedItems.push({
            fieldLabel: 'Equipment to Install',
            selectedText: option.field_title || option.field_value,
            price: option.price
          });
        }
      });
    }
    
    // 2. Equipment to Remove (string[])
    if (step2PropertyData.equipmentToBeRemoved && step2PropertyData.equipmentToBeRemoved.length > 0) {
      step2PropertyData.equipmentToBeRemoved.forEach((equipmentText) => {
        const option = cachedClientOptions?.find(
          item => item.field_key === 'equipment_removal' && item.field_value === equipmentText
        );
        if (option?.price && option.price > 0) {
          listBasedItems.push({
            fieldLabel: 'Equipment to Remove',
            selectedText: option.field_title || option.field_value,
            price: option.price
          });
        }
      });
    }
    
    // 3. Equipment to Keep (string[])
    if (step2PropertyData.equipmentToKeep && step2PropertyData.equipmentToKeep.length > 0) {
      step2PropertyData.equipmentToKeep.forEach((equipmentText) => {
        const option = cachedClientOptions?.find(
          item => item.field_key === 'equipment_keep' && item.field_value === equipmentText
        );
        if (option?.price && option.price > 0) {
          listBasedItems.push({
            fieldLabel: 'Equipment to Keep',
            selectedText: option.field_title || option.field_value,
            price: option.price
          });
        }
      });
    }
    
    // 4. Building Works (number[])
    if (step2PropertyData.buildingWorks && step2PropertyData.buildingWorks.length > 0) {
      step2PropertyData.buildingWorks.forEach((buildingWorkId) => {
        const option = cachedClientOptions?.find(
          item => item.field_key === 'building_work' && item.field_value === buildingWorkId.toString()
        );
        if (option?.price && option.price > 0) {
          listBasedItems.push({
            fieldLabel: 'Building Work',
            selectedText: option.field_title || option.field_value,
            price: option.price
          });
        }
      });
    }

  }
  
  // Add delivery cost from Step 6 if present
  const step6SignoffData = orderData?.step_data?.steps?.step6?.data;
  if (step6SignoffData && step6SignoffData.deliveryCost && step6SignoffData.deliveryCost > 0) {
    listBasedItems.push({
      fieldLabel: 'Delivery',
      selectedText: 'Delivery Cost',
      price: step6SignoffData.deliveryCost
    });
  }
  
  // Process Step 4 (Decorations) list-based items
  const step4Data = orderData?.step_data?.steps?.step4?.data;
  if (step4Data) {
    // 5. Accessories (number[])
    if (step4Data.accessories && step4Data.accessories.length > 0) {
      step4Data.accessories.forEach((accessoryId) => {
        const option = cachedClientOptions?.find(
          item => item.field_key === 'accessories' && item.field_value === accessoryId.toString()
        );
        if (option?.price && option.price > 0) {
          listBasedItems.push({
            fieldLabel: 'Accessory',
            selectedText: option.field_title || option.field_value,
            price: option.price
          });
        }
      });
    }
  }
  
  // Combine all items
  const allItems = [...itemsWithPrices, ...listBasedItems];
  
  if (allItems.length === 0) {
    return {
      summary: 'No items with prices selected yet.',
      total: 0
    };
  }
  
  let summaryText = 'ORDER SUMMARY\n';
  summaryText += '='.repeat(50) + '\n\n';
  
  let total = 0;
  
  allItems.forEach((item) => {
    const priceFormatted = `£${item.price.toFixed(2)}`;
    summaryText += `${item.fieldLabel}: ${item.selectedText}\n`;
    summaryText += `  ${priceFormatted}\n\n`;
    total += item.price;
  });
  
  summaryText += '-'.repeat(50) + '\n';
  summaryText += `SUBTOTAL: £${total.toFixed(2)}\n`;
  
  console.log(`[generateOrderSummary] Total: £${total.toFixed(2)}, Items: ${allItems.length} (${itemsWithPrices.length} dropdowns + ${listBasedItems.length} list items)`);
  
  return {
    summary: summaryText,
    total
  };
}

/**
 * Update the order summary and total price in orderData
 * This should be called whenever navigating between steps or closing the wizard
 */
function updateOrderSummaryAndTotal() {
  //console.log('[updateOrderSummaryAndTotal] Recalculating order summary and total');
  
  const { summary, total } = generateOrderSummary();
  
  // Update orderData with new summary and total
  orderData.step_data.steps.step6.data.orderSummary = summary;
  orderData.totalContractPrice = total;
  
  //console.log(`[updateOrderSummaryAndTotal] Updated - Total: £${total.toFixed(2)}, Items: ${summary.split('\n').length - 4}`);
}

/**
 * Rebuild the storedPrices Map from saved orderData
 * This should be called when loading an existing order to restore price tracking
 */
function rebuildStoredPricesFromOrderData() {
  //console.log('[rebuildStoredPricesFromOrderData] Rebuilding storedPrices from saved order data');
  
  // Clear existing stored prices
  storedPrices.clear();
  
  if (!orderData || !orderData.step_data || !cachedClientOptions) {
    //console.warn('[rebuildStoredPricesFromOrderData] Missing required data');
    return;
  }
  
  // Helper function to add a price item if it has a price
  const addPriceItem = (fieldKey: string, fieldValue: string | number | boolean, fieldLabel: string) => {
    if (!fieldValue || fieldValue === '' || fieldValue === 0) {
      return; // Skip empty values
    }
    
    // Find the option in cached client options
    const option = cachedClientOptions?.find(
      item => item.field_key === fieldKey && item.field_value === fieldValue.toString()
    );
    
    if (option && option.price !== null && option.price > 0) {
      storedPrices.set(fieldKey, {
        fieldKey,
        fieldLabel,
        selectedValue: option.field_value,
        selectedText: option.field_title || option.field_value,
        price: option.price
      });
      //console.log(`[rebuildStoredPricesFromOrderData] Restored ${fieldKey}: ${option.field_title || option.field_value} = £${option.price}`);
    }
  };
  
  // Extract prices from Step 1 (Customer) - typically no prices here
  // const step1 = orderData.step_data.steps.step1.data;
  
  // Extract prices from Step 2 (Property)
  const step2 = orderData.step_data.steps.step2.data.propertyData;
  if (step2) {
    if (step2.propertyType) addPriceItem('property_types', step2.propertyType, 'Property Type');
    if (step2.materialStorageLocation) addPriceItem('material_storage_location', step2.materialStorageLocation, 'Material Storage Location');
    if (step2.rubbishLocation) addPriceItem('rubbish_location', step2.rubbishLocation, 'Rubbish Location');
    if (step2.floorConstruction) addPriceItem('floor_construction', step2.floorConstruction, 'Floor Construction');
    if (step2.heatingSystem) addPriceItem('heating_system', step2.heatingSystem, 'Heating System');
    if (step2.boilerType) addPriceItem('boiler_type', step2.boilerType, 'Boiler Type');

    if (step2.vanityUnit) addPriceItem('vanity_unit', step2.vanityUnit, 'Vanity Unit');
    if (step2.wcUnit) addPriceItem('wc_unit', step2.wcUnit, 'WC Unit');
    if (step2.cloakroomPack) addPriceItem('cloakroom_pack', step2.cloakroomPack, 'Cloakroom Pack');
    if (step2.bidet) addPriceItem('bidet', step2.bidet, 'Bidet');
    if (step2.newPosition) addPriceItem('new_position', step2.newPosition, 'New Position');
    if (step2.basinTap) addPriceItem('basin_tap', step2.basinTap, 'Basin Tap');
  }
  
  // Extract prices from Step 3 (Products)
  const step3 = orderData.step_data.steps.step3.data;
  if (step3) {
    if (step3.productType) addPriceItem('product_type', step3.productType, 'Product Type');
    if (step3.productRange) addPriceItem('product_range', step3.productRange, 'Product Range');
    if (step3.productVariant) addPriceItem('product_variant', step3.productVariant, 'Product Variant');
    if (step3.productScreenOption) addPriceItem('product_bath_screen_option', step3.productScreenOption, 'Screen Option');
    if (step3.productTap) addPriceItem('tap', step3.productTap, 'Tap');
    if (step3.productBoxSection) addPriceItem('product_box_section', step3.productBoxSection, 'Box Section');
    if (step3.productEndPanel) addPriceItem('end_panel', step3.productEndPanel, 'End Panel');
    if (step3.productShowerTraySize) addPriceItem('product_shower_tray_size', step3.productShowerTraySize, 'Shower Tray Size');
    if (step3.productScreenSize) addPriceItem('product_shower_screen_size', step3.productScreenSize, 'Screen Size');
    if (step3.productScreenSidePanel) addPriceItem('product_screen_side_panel', step3.productScreenSidePanel, 'Screen Side Panel');
    if (step3.productShowerType) addPriceItem('shower_type', step3.productShowerType, 'Shower Type');
    if (step3.productSeat) addPriceItem('product_seat', step3.productSeat, 'Seat');
    if (step3.wasteBooster) addPriceItem('waste_booster', step3.wasteBooster, 'Waste Booster');
    if (step3.wasteBoosterRequired) addPriceItem('waste_booster_required', step3.wasteBoosterRequired, 'Waste Booster Required');
    if (step3.electrianRequired) addPriceItem('electrician_required', step3.electrianRequired, 'Electrician Required');
    if (step3.wastePump) addPriceItem('waste_pump', step3.wastePump, 'Waste Pump');
    if (step3.wastePumpRequired) addPriceItem('waste_pump_required', step3.wastePumpRequired, 'Waste Pump Required');
  }
  
  // Extract prices from Step 4 (Decorations)
  const step4 = orderData.step_data.steps.step4.data;
  if (step4) {
    if (step4.wallboardPanelColour) addPriceItem('wallboard_colour', step4.wallboardPanelColour, 'Wallboard Panel Colour');
    if (step4.wallboardPanelTrims) addPriceItem('wallboard_trims', step4.wallboardPanelTrims, 'Wallboard Trims');
    if (step4.wallboardPanelWholeBathroom) addPriceItem('wallboard_whole_bathroom', step4.wallboardPanelWholeBathroom, 'Whole Bathroom');
    if (step4.wallboardPanelAdditionalBoard) addPriceItem('wallboard_additional_boards', step4.wallboardPanelAdditionalBoard, 'Additional Boards');
    if (step4.floorType) addPriceItem('flooring_type', step4.floorType, 'Floor Type');
    if (step4.floorColour) addPriceItem('floor_colour', step4.floorColour, 'Floor Colour');
    if (step4.additionalDownlighters) addPriceItem('additional_downlighters', step4.additionalDownlighters, 'Additional Downlighters');
    if (step4.grabRails) addPriceItem('grab_rails', step4.grabRails, 'Grab Rails');
    if (step4.grabRailsReq) addPriceItem('grab_rails_required', step4.grabRailsReq, 'Grab Rails Required');
    if (step4.supportRails) addPriceItem('support_rails', step4.supportRails, 'Support Rails');
    if (step4.floorToCeilingPole) addPriceItem('floor_to_ceiling_pole', step4.floorToCeilingPole, 'Floor to ceiling pole');
    if (step4.heatedTowelRail) addPriceItem('heated_towel_rail', step4.heatedTowelRail, 'Heated Towel Rail');
    if (step4.mirrors) addPriceItem('mirrors', step4.mirrors, 'Mirrors');
    if (step4.saniflow) addPriceItem('saniflow', step4.saniflow, 'Saniflow');
    if (step4.ceilingPanels) addPriceItem('ceiling_panels', step4.ceilingPanels, 'Ceiling Panels');
    

  }
  
  // Extract prices from Step 6 (Signoff)
  const step6 = orderData.step_data.steps.step6.data;
  if (step6 && step6.salesGroup) {
    addPriceItem('sales_group', step6.salesGroup, 'Sales Group');
  }
  
  //console.log(`[rebuildStoredPricesFromOrderData] Rebuilt ${storedPrices.size} price items from saved data`);
}




// Create step progress indicator
function createStepProgress(): string {
  let progressHTML = '<div class="step-progress-container"><ul class="step-progress">';
  
  for (let i = 0; i < 6; i++) {
    const stepClass = i === currentStep ? 'active' : (i < currentStep ? 'completed' : '');
    progressHTML += `<li class="step-progress-item ${stepClass}" title="${stepTitles[i]}" >${i + 1}</li>`;
  }
  
  const stepCloseOption = `<div class="step-close"><button class="wizard-exit">X</button></div>`;

  progressHTML += `</ul><div class="step-title">${stepTitles[currentStep]}</div>${stepCloseOption}</div>`;
  return progressHTML;
}

// Update header with step progress
function updateStepProgress() {
  const headerContent = document.querySelector('.header-content');
  if (headerContent) {
    // Remove existing step progress if any
    const existingProgress = headerContent.querySelector('.step-progress-container');
    if (existingProgress) {
      existingProgress.remove();
    }
    
    // Add new step progress
    const progressElement = document.createElement('div');
    progressElement.innerHTML = createStepProgress();
    headerContent.appendChild(progressElement.firstElementChild!);
  }
}

// Create wizard step components
function createCustomerForm(customerData?: Wizard1Customer): HTMLElement {
  const defaultAddress: Address = getDefaultAddress();
  const customer = customerData?.customerData || {
    name: '',
    customerAddress: defaultAddress,
    installationAddress: defaultAddress,
    sameAsCustomerAddress: true
  } as Customer;

  const form = document.createElement('form');
  form.className = 'step-card';
  form.innerHTML = `
  <div class="wizard-right">
    <h3>Installation Address</h3>
    <div class="form-group">
      <label for="sameAs" class="check-wrap">
        <input type="checkbox" class="checkbox-input" id="sameAs" name="sameAs" ${customer.sameAsCustomerAddress ? 'checked' : ''} />

        <span class="check-ui"></span>
        Same as Customer Address
      </label>  
    </div>
    <div class="form-group">
      <div class="column-2">
        <label for="instHouseNo">House No</label>
        <input type="text" id="instHouseNo" name="instHouseNo" placeholder="House No" value="${customer.installationAddress?.houseNo || ''}">
      </div>
      <div class="column-2">
        <label for="instStreet">Street</label>
        <input type="text" id="instStreet" name="instStreet" placeholder="Street" value="${customer.installationAddress?.street || ''}">
      </div>
    </div>
    <div class="form-group">
      <label for="instCity">Town</label>
      <input type="text" id="instCity" name="instCity" placeholder="Town" value="${customer.installationAddress?.city || ''}">
    </div>
    <div class="form-group">
      <label for="instCounty">County</label>
      <select
        id="instCounty" 
        name="instCounty" 
        data-orderoptions="counties"
        data-orderlocal="true"
        data-selected-value="${customer.installationAddress?.county || ''}" 
        placeholder="County" >
        <option value="">Select County</option>
      </select>
    </div>
    <div class="form-group">
      <label for="instCountry">Country</label>
      <select 
        id="instCountry" 
        name="instCountry" 
        data-orderoptions="countries" 
        data-selected-value="${customer.installationAddress?.country || ''}" 
        placeholder="Country" 
        required>
          <option value="">Select Country</option>
      </select>
    </div>
    <div class="form-group">
      <label for="instPostcode">Postcode</label>
      <input type="text" id="instPostcode" name="instPostcode" placeholder="Postcode" value="${customer.installationAddress?.postcode || ''}">
    </div>
  </div>
  <div class="wizard-left"> 
    <h3>Customer Address</h3>
    <div class="form-group">
      <label for="customerName">Name</label>
      <input type="text" id="customerName" name="customerName" placeholder="Enter customer name" required value="${customer.name || ''}">
    </div>
    <div class="form-group">
      <div class="column-2">
        <label for="custHouseNo">House No</label>
        <input type="text" id="custHouseNo" name="custHouseNo" placeholder="House No" required value="${customer.customerAddress?.houseNo || ''}">
      </div>
      <div class="column-2">
        <label for="custStreet">Street</label>
        <input type="text" id="custStreet" name="custStreet" placeholder="Street" required value="${customer.customerAddress?.street || ''}">
      </div>
    </div>
    <div class="form-group">
      <label for="custCity">Town</label>
      <input type="text" id="custCity" name="custCity" placeholder="Town" required value="${customer.customerAddress?.city || ''}">
    </div>
    <div class="form-group">
      <label for="custCounty">County</label>
      <select
        id="custCounty" 
        name="custCounty" 
        data-orderoptions="counties"
        data-orderlocal="true"
        data-selected-value="${customer.customerAddress?.county || ''}" 
        placeholder="County" >
        <option value="">Select County</option>
      </select>
    </div>
    <div class="form-group">
      <label for="custCountry">Country</label>
      <select 
        id="custCountry" 
        name="custCountry" 
        data-orderoptions="countries" 
        data-selected-value="${customer.customerAddress?.country || ''}" 
        placeholder="Country" 
        required>
          <option value="">Select Country</option>
      </select>
    </div>
    <div class="form-group">
      <label for="custPostcode">Postcode</label>
      <input type="text" id="custPostcode" name="custPostcode" placeholder="Postcode" required value="${customer.customerAddress?.postcode || ''}">
    </div>
    <div class="form-group">
      <label for="custPhone">Phone</label>
      <input type="tel" id="custPhone" name="custPhone" placeholder="Phone" value="${customer.phone || ''}">
    </div>
    <div class="form-group">
      <label for="custEmail">Email</label>
      <input type="email" id="custEmail" name="custEmail" placeholder="Email" value="${customer.email || ''}">
    </div>
  </div>
  
  `;
  return form;
}

function createPropertyForm(propertyData?: Wizard2Property): HTMLElement {
  const property = propertyData?.propertyData || {} as propertyDetails;
  
  const form = document.createElement('form');
  form.className = 'step-card';
  form.innerHTML = `
  <div class="wizard-right">
    <h3>Equipment to be installed</h3>
    <div class="form-group">
      <label for="equipmentToBeInstalled">Install</label>
      <div style="display: flex; gap: 10px; margin-bottom: 10px;">
        <select 
          id="equipmentToBeInstalled" 
          name="equipmentToBeInstalled" 
          data-orderoptions="equipment_installation" 
          style="flex: 1;">
        </select>
        <button type="button" id="addEquipmentInstallButton" style="min-width: 80px;">Add</button>
      </div>
      <listbox 
        id="equipmentInstallList" 
        name="equipmentInstallList" style="width:100%;" >
      </listbox>
    </div>
  </div>
  <div class="wizard-left">
      <h3>Floor Construction</h3>
      <div class="form-group">
        <label for="floorConstruction">Floor Construction</label>
        <select 
          id="floorConstruction" 
          name="floorConstruction" 
          data-orderoptions="floor_construction" 
          data-selected-value="${property.floorConstruction || ''}"
          placeholder="Floor Construction"
          required>
          <option value="">Select type</option>
        </select>
      </div>
      <div class="form-group">
        <div class="column-2">
          <label for="ceilingHeight">Ceiling Height</label>
          <input type="number" id="ceilingHeight" name="ceilingHeight" placeholder="Ceiling Height" value="${property.ceilingHeight || ''}">
        </div>
        <div class="column-2">
          <label for="wallboardTerminationHeight">Wallboard Height</label>
          <input type="number" id="wallboardTerminationHeight" name="wallboardTerminationHeight" placeholder="Wallboard Height" value="${property.wallboardTerminationHeight || ''}">
        </div>
      </div>
      <div class="form-group">
        <label for="rubbishLocation">Rubbish Location</label>
        <select 
          id="rubbishLocation" 
          name="rubbishLocation" 
          data-orderoptions="rubbish_location" 
          data-selected-value="${property.rubbishLocation || ''}"
          placeholder="Rubbish Location"
          required>
          <option value="">Select type</option>
        </select>
      </div>
    </div>
    <div class="wizard-right">
      <h3>Equipment to be removed</h3>
      <div class="form-group">
        <label for="equipmentToBeRemoved">Remove</label>
        <div style="display: flex; gap: 10px; margin-bottom: 10px;">
          <select 
            id="equipmentToBeRemoved" 
            name="equipmentToBeRemoved" 
            data-orderoptions="equipment_removal" 
            style="flex: 1;">
          </select>
          <button type="button" id="addEquipmentRemoveButton" style="min-width: 80px;">Add</button>
        </div>
        <listbox 
          id="equipmentRemoveList" 
          name="equipmentRemoveList" style="width:100%;" >
        </listbox>
      </div>
    </div>
    <div class="wizard-left">
      <h3>Property Details</h3>
      <div class="form-group">
        <label for="propertyType">Property Type</label>
        <select id="propertyType" name="propertyType" data-orderoptions="property_types" data-selected-value="${property.propertyType || ''}" required>
          <option value="">Select type</option>
        </select>
      </div>
      <div class="form-group">
        <label for="materialStorageLocation">Material Storage Location</label>
        <select 
          id="materialStorageLocation" 
          name="materialStorageLocation" 
          data-orderoptions="material_storage_location" 
          data-selected-value="${property.materialStorageLocation || ''}"
          placeholder="Material Storage Location"
          required>
          <option value="">Select type</option>
        </select>
      </div>
      <div class="form-group">
        <div class="column-2">
          <label for="permissionRequired">Permission Required</label>
          <select id="permissionRequired" name="permissionRequired">
            <option value="yes" ${property.permissionRequired ? 'selected' : ''}>Yes</option>
            <option value="no" ${!property.permissionRequired ? 'selected' : ''}>No</option>
          </select>
        </div>
        <div class="column-2">
          <label for="parkingPermitRequired">Parking Permit</label>
          <select id="parkingPermitRequired" name="parkingPermitRequired">
            <option value="yes" ${property.parkingPermitRequired ? 'selected' : ''}>Yes</option>
            <option value="no" ${!property.parkingPermitRequired ? 'selected' : ''}>No</option>
          </select>
        </div>
        
      </div>
    </div>
    <div class="wizard-right">
      <h3>Equipment to keep in position</h3>
      <div class="form-group">
        <label for="equipmentToKeep">Keep</label>
        <div style="display: flex; gap: 10px; margin-bottom: 10px;">
          <select 
            id="equipmentToKeep" 
            name="equipmentToKeep" 
            data-orderoptions="equipment_keep" 
            style="flex: 1;">
          </select>
          <button type="button" id="addEquipmentKeepButton" style="min-width: 80px;">Add</button>
        </div>
        <listbox 
          id="equipmentKeepList" 
          name="equipmentKeepList" style="width:100%;" >
        </listbox>
      </div>
    </div>
    <div class="wizard-left">
      <h3>Water System</h3>
      <div class="form-group">
        <label for="heatingSystem">Heating System</label>
        <select 
          id="heatingSystem" 
          name="heatingSystem" 
          data-orderoptions="heating_system" 
          data-selected-value="${property.heatingSystem || ''}" required>
          <option value="">Select type</option>
        </select>
      </div>
      <div class="form-group">
        <label for="boilerType">Boiler Type</label>
        <select 
          id="boilerType" 
          name="boilerType" 
          data-orderoptions="boiler_type" 
          data-selected-value="${property.boilerType || ''}" required>
          <option value="">Select type</option>
        </select>
      </div>
      <div class="form-group">
        <label for="boilerModel">Boiler Model</label>
        <input 
          type="text"
          id="boilerModel" 
          name="boilerModel" 
          value="${property.boilerModel || ''}" />
      </div>

      <div class="form-group">
        <div class="column-2">
          <label for="cylinderCapacity">Cylinder Capacity</label>
          <input type="text" id="cylinderCapacity" name="cylinderCapacity" placeholder="Cylinder Capacity" value="${property.cylinderCapacity || ''}" />
        </div>
        <div class="column-2">
          <label for="hotWaterFlowRate">Hot Water Flow Rate</label>
          <input type="text" id="hotWaterFlowRate" name="hotWaterFlowRate" placeholder="Hot Water Flow Rate" value="${property.hotWaterFlowRate || ''}" />
        </div>
      </div>

      <div class="form-group">
        <div class="column-2">
          <label for="hotTankSizeW">Hot Water Tank Size (w)</label>
          <input type="text" id="hotTankSizeW" name="hotTankSizeW" placeholder="Tank Width" value="${property.hotTankSizeW || ''}" />
        </div>
        <div class="column-2">
          <label for="hotTankSizeH">Hot Water Tank Size (H)</label>
          <input type="text" id="hotTankSizeH" name="hotTankSizeH" placeholder="Tank Height" value="${property.hotTankSizeH || ''}" />
        </div>
      </div>
    </div>
    <div class="wizard-right">
      <h3>Building Work</h3>
      <div class="form-group">
        <label for="buildingwork">Works</label>
        <div style="display: flex; gap: 10px; margin-bottom: 10px;">
          <select 
            id="buildingwork" 
            name="buildingwork" 
            data-orderoptions="building_work" 
            style="flex: 1;">
          </select>
          <button type="button" id="addBuildingworkButton" style="min-width: 80px;">Add</button>
        </div>
        <listbox 
          id="buildingworkList" 
          name="buildingworkList" style="width:100%;" >
        </listbox>
      </div>
    </div>
    <div class="wizard-left">
      <h3>Furniture</h3>
      <div class="form-group">
        <label for="vanityUnit">Vanity Unit</label>
        <select 
          id="vanityUnit" 
          name="vanityUnit" 
          data-orderoptions="vanity_unit" 
          data-selected-value="${property.vanityUnit || ''}" >
          <option value="">Please Select</option>
        </select>
      </div>
      <div class="form-group">
        <label for="wcUnit">WC Unit</label>
        <select 
          id="wcUnit" 
          name="wcUnit" 
          data-orderoptions="wc_unit" 
          data-selected-value="${property.wcUnit || ''}" >
          <option value="">Please Select</option>
        </select>
      </div>
      <div class="form-group">
        <label for="cloakroomPack">Cloakroom Pack</label>
        <select 
          id="cloakroomPack" 
          name="cloakroomPack" 
          data-orderoptions="cloakroom_pack" 
          data-selected-value="${property.cloakroomPack || ''}" >
          <option value="">Please Select</option>
        </select>
      </div>
      <div class="form-group">
        <label for="bidet">Bidet</label>
        <select 
          id="bidet" 
          name="bidet" 
          data-orderoptions="bidet" 
          data-selected-value="${property.bidet || ''}" >
          <option value="">Please Select</option>
        </select>
      </div>
      <div class="form-group">
        <label for="newPosition">New Position</label>
        <select 
          id="newPosition" 
          name="newPosition" 
          data-orderoptions="new_position" 
          data-selected-value="${property.newPosition || ''}" >
          <option value="">Please Select</option>
        </select>
      </div>
      <div class="form-group">
        <label for="basinTap">Basin Tap</label>
        <select 
          id="basinTap" 
          name="basinTap" 
          data-orderoptions="basin_tap" 
          data-selected-value="${property.basinTap || ''}" >
          <option value="">Please Select</option>
        </select>
      </div>
    </div>
  `;
  return form;
}

function createProductForm(productData?: Wizard3Products): HTMLElement {
  const form = document.createElement('form');
  form.className = 'step-card';
  //const products = await fetchProductsFromAPI();
  //${products.map(p => `<option value="${p.name}" ${productData?.productType === p.name ? 'selected' : ''}>${p.name} - £${p.price}</option>`).join('')}
  form.innerHTML = `
  <div class="wizard-full">
    <h3>Product Selection</h3> 
    <div class="form-group">
      <label for="productType">Product Type</label>
      <select 
        id="productType" 
        name="productType" 
        data-orderoptions="product_type" 
        data-selected-value="${productData?.productType || ''}"
        placeholder="Product Type"
        required>
        <option value="">Select type</option>
      </select>
    </div>
    <div class="form-group">
      <label for="productRangle">Range</label>
      <select 
        id="productRange" 
        name="productRange" 
        data-orderoptions="product_range" 
        data-selected-value="${productData?.productRange || 0}"
        placeholder="Product Range"
        required>
        <option value="">Select Range</option>
      </select>
    </div>
    <div class="form-group">
      <label for="productModel">Model</label>
      <select 
        id="productVariant" 
        name="productVariant" 
        data-orderoptions="product_variant" 
        data-selected-value="${productData?.productVariant || 0}"
        placeholder="Product Model"
        required>
        <option value="">Select Model</option>
      </select>
    </div>
    <div id="pack-summary" class="mt10"></div>
    <div id="pack-debug" class="hidden"></div>
    <div id="pack-validation" class="warning-text"></div>
    <div id="validation-errors" class="warning-text"></div>
  </div>
  <div class="wizard-full" id="product-details-bath">
    <h3>Product Details - Bath</h3> 
    <div class="form-group">
      <label for="productScreen">Screen</label>
      <select 
        id="productScreenOption" 
        name="productScreenOption" 
        data-orderoptions="product_bath_screen_option" 
        data-pack-key="bath_screen"
        data-selected-value="${productData?.productScreenOption || 0}"
        placeholder="Product Screen Option"
        >
        <option value="">Select Bath Screen</option>
      </select>
    </div>
    <div class="form-group">
      <label for="productBoxSection">Box Section</label>
      <select 
        id="productBoxSection" 
        name="productBoxSection" 
        data-orderoptions="product_box_section" 
        data-selected-value="${productData?.productBoxSection || ''}"
        placeholder="Box Section"
        >
      </select>
    </div>
    <div class="form-group">
      <label for="productTap">Tap</label>
      <select 
        id="productTap" 
        name="productTap" 
        data-orderoptions="tap" 
        data-selected-value="${productData?.productTap || ''}"
        placeholder="Tap"
        >
      </select>
    </div>
    <div class="form-group">
      <label for="productEndPanel">End Panel</label>
      <select 
        id="productEndPanel" 
        name="productEndPanel" 
        data-orderoptions="end_panel" 
        data-pack-key="end_panel"
        data-selected-value="${productData?.productEndPanel || ''}"
        placeholder="End Panel?" >
        <option value="">Select End Panel</option>
      </select>
    </div>
  </div>
  <div class="wizard-full" id="product-details-shower">
    <h3>Product Details - Shower</h3> 
    <div class="form-group">
      <label for="productShowerTraySize">Shower Tray Size</label>
      <select 
        id="productShowerTraySize" 
        name="productShowerTraySize" 
        data-orderoptions="product_shower_tray_size" 
        data-pack-key="shower_tray"
        data-selected-value="${productData?.productShowerTraySize || ''}" 
        >
        <option value="">Select Shower Tray</option>
      </select>
    </div>
    <div class="form-group">
      <label for="productScreenSize">Shower Screen Size</label>
      <select 
        id="productScreenSize" 
        name="productScreenSize" 
        data-orderoptions="product_shower_screen_size" 
        data-pack-key="shower_screen"
        data-selected-value="${productData?.productScreenSize || ''}" 
        >
        <option value="">Select Shower Screen Size</option>
      </select>
    </div>
    <div class="form-group">
      <label for="productScreenSidePanel">Shower Screen Side Panel</label>
      <select 
        id="productScreenSidePanel" 
        name="productScreenSidePanel" 
        data-orderoptions="product_screen_side_panel" 
        data-pack-key="shower_panel"
        data-selected-value="${productData?.productScreenSidePanel || ''}" 
        >
        <option value="">Select Shower Panel</option>
      </select>
    </div>
    <div class="form-group">
      <label for="productShowerType">Shower Type</label>
      <select 
        id="productShowerType" 
        name="productShowerType" 
        data-orderoptions="shower_type" 
        data-selected-value="${productData?.productShowerType || ''}" 
        >
      </select>
    </div>
  </div>
  <div class="wizard-full" >
    <div class="form-group">
      <label for="productSeat">Seat</label>
      <select 
        id="productSeat" 
        name="productSeat" 
        data-orderoptions="product_seat" 
        data-pack-key="seats"
        data-selected-value="${productData?.productSeat || ''}" 
        >
        <option value="">Select Seat</option>
      </select>
    </div>
  </div>
  <div class="wizard-full">
    <div class="form-group">
      <div class="column-2">
        <label for="wasteBooster">Waste Booster</label>
        <select 
          id="wasteBooster" 
          name="wasteBooster" 
          data-orderoptions="waste_booster" 
          data-selected-value="${productData?.wasteBooster || ''}"
          placeholder="Waste Booster"
          >
          <option value="">N/A</option>
        </select>
      </div>
      <div class="column-2">
        <label for="wasteBoosterRequired">Waste Booster Required?</label>
        <select 
          id="wasteBoosterRequired" 
          name="wasteBoosterRequired" 
          data-orderoptions="waste_booster_required" 
          data-selected-value="${productData?.wasteBoosterRequired || ''}"
          placeholder="Waste Booster Required?" >
        </select>
      </div>
    </div>
    <div class="form-group">
      <div class="column-2">
        <label for="wastePump">Waste Pump</label>
        <select 
          id="wastePump" 
          name="wastePump" 
          data-orderoptions="waste_pump" 
          data-selected-value="${productData?.wastePump || ''}"
          placeholder="Waste Pump"
          >
          <option value="">N/A</option>
        </select>
      </div>
      <div class="column-2">
        <label for="wastePumpRequired">Waste Pump Required?</label>
        <select 
          id="wastePumpRequired" 
          name="wastePumpRequired" 
          data-orderoptions="waste_pump_required" 
          data-selected-value="${productData?.wastePumpRequired || ''}"
          placeholder="Waste Pump Required?" >
        </select>
      </div>
    </div>
  </div>
  <div class="wizard-full">
    <div class="form-group">
      <label for="electrianRequired">Electrician Required</label>
      <select 
        id="electrianRequired" 
        name="electrianRequired" 
        data-orderoptions="electrician_required" 
        data-selected-value="${productData?.electrianRequired || ''}" 
        placeholder="Electrian Required?" >
      </select>
    </div>
  </div>`;
  return form;
}

function createDecorationForm(decorationData?: Wizard4Decorations): HTMLElement {
  const form = document.createElement('form');
  form.className = 'step-card';
  form.innerHTML = `
  <div class="wizard-right">
    <h3>Lighting and Extrator Fans Panel</h3>
    <div class="form-group">
      <div class="column-2">
        <label for="extractorFan">Extractor Fan</label>
        <select id="extractorFan" name="extractorFan">
          <option value="yes" ${decorationData?.extractorFan ? 'selected' : ''}>Yes</option>
          <option value="no" ${!decorationData?.extractorFan ? 'selected' : ''}>No</option>
        </select>
      </div>
      <div class="column-2">
        <label for="centralCeilingLight">Central Ceiling Light</label>
        <select id="centralCeilingLight" name="centralCeilingLight">
          <option value="yes" ${decorationData?.centralCeilingLight ? 'selected' : ''}>Yes</option>
          <option value="no" ${!decorationData?.centralCeilingLight ? 'selected' : ''}>No</option>
        </select>
      </div>
    </div>
    <div class="form-group">
      <label for="additionalDownlighters">Additional Downlightters</label>
      <select 
        id="additionalDownlighters" 
        name="additionalDownlighters" 
        data-orderoptions="additional_downlighters" 
        data-selected-value="${decorationData?.additionalDownlighters || ''}"
        placeholder="Additional Downlightters"
        required>
      </select>
    </div>
  </div>
  <div class="wizard-left">
    <h3>Decoration Details</h3>
    <div class="form-group">
      <label for="wallboardPanelColour">Wallboard Panel Colour</label>
      <select 
        id="wallboardPanelColour" 
        name="wallboardPanelColour" 
        data-orderoptions="wallboard_colour" 
        data-selected-value="${decorationData?.wallboardPanelColour || ''}"
        placeholder="Wallboard colour"
        >
        <option value="">N/A</option>
      </select>
    </div>
    <div class="form-group">
      <div class="column-2">
        <label for="wallboardPanelLinearMeters">Wallboard Linear Meters</label>
        <input 
          type="number" 
          id="wallboardPanelLinearMeters" 
          name="wallboardPanelLinearMeters" 
          placeholder="Linear meters" 
          value="${decorationData?.wallboardPanelLinearMeters || ''}" />
      </div>
      <div class="column-2">
        <label for="wallboardPanelTrims">Wallboard Trims</label>
        <select 
          id="wallboardPanelTrims" 
          name="wallboardPanelTrims" 
          data-orderoptions="wallboard_trims" 
          data-selected-value="${decorationData?.wallboardPanelTrims || ''}"
          placeholder="Wallboard Trims"
          required>
          <option value="">Please Select</option>
        </select>
      </div>
    </div>
    <div class="form-group">
      <div class="column-2">
        <label for="wallboardPanelColour">Whole Bathroom</label>
        <select 
          id="wallboardPanelWholeBathroom" 
          name="wallboardPanelWholeBathroom" 
          data-orderoptions="wallboard_whole_bathroom" 
          data-selected-value="${decorationData?.wallboardPanelWholeBathroom || ''}"
          placeholder="Wallboard colour"
          >
        </select>
      </div>
      <div class="column-2">
        <label for="wallboardPanelAdditionalBoard">Additional Boards</label>
        <select 
          id="wallboardPanelAdditionalBoard" 
          name="wallboardPanelAdditionalBoard" 
          data-orderoptions="wallboard_additional_boards" 
          data-selected-value="${decorationData?.wallboardPanelAdditionalBoard || ''}"
          placeholder="Additional Boards"
          >
        </select>
      </div>
    </div>
  </div>
  <div class="wizard-right">
    <h3>Optional Accessories</h3>
    <div class="form-group">
      <label for="accessories">Accessories</label>
      <div style="display: flex; gap: 10px; margin-bottom: 10px;">
        <select 
          id="accessories" 
          name="accessories" 
          data-orderoptions="accessories" 
          style="flex: 1;">
        </select>
        <button type="button" id="addAccessoriesButton" style="min-width: 80px;">Add</button>
      </div>
      <listbox 
        id="accessoriesList" 
        name="accessoriesList" style="width:100%;" >
      </listbox>
    </div>
  </div>
  <div class="wizard-left">
    <h3>Floor and Ceiling Panel</h3>
    <div class="form-group">
      <div class="column-2">
        <label for="floorType">Floor Type</label>
        <select 
          id="floorType" 
          name="floorType" 
          data-orderoptions="flooring_type" 
          data-selected-value="${decorationData?.floorType || ''}"
          placeholder="Flooring Type"
          >
          <option value="">N/A</option>
        </select>
      </div>
      <div class="column-2">
        <label for="floorColour">Flooring Colour</label>
        <select 
          id="floorColour" 
          name="floorColour" 
          data-orderoptions="floor_colour" 
          data-selected-value="${decorationData?.floorColour || ''}"
          placeholder="Flooring Colour"
          >
          <option value="">N/A</option>
        </select>
      </div>
    </div>
    <div class="form-group">
      <div class="column-2">
        <label for="floorType">Ceiling Panels</label>
        <select 
          id="ceilingPanels" 
          name="ceilingPanels" 
          data-orderoptions="ceiling_panels" 
          data-selected-value="${decorationData?.ceilingPanels || ''}"
          placeholder="Ceiling Panels"
          >
          <option value="">N/A</option>
        </select>
      </div>
      <div class="column-2">
      </div>
    </div>
  </div>
  <div class="wizard-right">
    <h3>Grab Rails</h3>
    <div class="form-group">
      <div class="column-2">
        <label for="grabRails">Grab Rails</label>
        <select 
          id="grabRails" 
          name="grabRails" 
          data-orderoptions="grab_rails" 
          data-selected-value="${decorationData?.grabRails || ''}" 
          >
          <option value="">Please Select</option>
        </select>
      </div>
      <div class="column-2">
        <label for="grabRailsReq">Grab Rails Required</label>
        <select 
          id="grabRailsReq" 
          name="grabRailsReq" 
          data-orderoptions="grab_rails_required" 
          data-selected-value="${decorationData?.grabRailsReq || ''}" 
          >
          <option value="">Please Select</option>
        </select>
      </div>
    </div>
    <div class="form-group">
      <div class="column-2">
        <label for="supportRails">Support Rails</label>
        <select 
          id="supportRails" 
          name="supportRails" 
          data-orderoptions="support_rails" 
          data-selected-value="${decorationData?.supportRails || ''}" 
          >
          <option value="">Please Select</option>
        </select>
      </div>
      <div class="column-2">
        <label for="floorToCeilingPole">Floor To Ceiling Pole</label>
        <select 
          id="floorToCeilingPole" 
          name="floorToCeilingPole" 
          data-orderoptions="floor_to_ceiling_pole" 
          data-selected-value="${decorationData?.floorToCeilingPole || ''}" 
          >
          <option value="">Please Select</option>
        </select>
      </div>
    </div>
  </div>
  <div class="wizard-left">
    <h3>Heated Towel Rail/Mirrors/Saniflow</h3>
    <div class="form-group">
      <div class="column-2">
        <label for="heatedTowelRail">Heated Towel Rail</label>
        <select 
          id="heatedTowelRail" 
          name="heatedTowelRail" 
          data-orderoptions="heated_towel_rail" 
          data-selected-value="${decorationData?.heatedTowelRail || ''}" 
          >
          <option value="">Please Select</option>
        </select>
      </div>
      <div class="column-2">
        <label for="mirrors">Mirrors</label>
        <select 
          id="mirrors" 
          name="mirrors" 
          data-orderoptions="mirrors" 
          data-selected-value="${decorationData?.mirrors || ''}" 
          >
          <option value="">Please Select</option>
        </select>
      </div>
    </div>
    <div class="form-group">
      <div class="column-2">
        <label for="saniflow">Saniflow</label>
        <select 
          id="saniflow" 
          name="saniflow" 
          data-orderoptions="saniflow" 
          data-selected-value="${decorationData?.saniflow || ''}" 
          >
          <option value="">Please Select</option>
        </select>
      </div>
    </div>
  </div>
  `;
  return form;
}

function createPhotosDrawingForm(drawingData?: Wizard5Drawings): HTMLElement {

  const newPhotoButton = '<button type="button" class="add-photo-btn">+</button>';
  const removePhotoButton = '<button type="button" class="remove-photo-btn" style="display: none;">−</button>';

  //console.log('Creating Photos and Drawings form with data:', drawingData?.photos, drawingData?.drawingData.objects2d);
  const numberOfItems: number = (drawingData 
    && drawingData.drawingData 
    && drawingData.drawingData.objects2d) 
    ? drawingData.drawingData.objects2d.length : 0;

  const container = document.createElement('div');
  container.className = 'step-card photos-drawings-container';
  container.innerHTML = `
    <div class="wizard-left" style="width: 100%;">
      <h3>Photos</h3>
      <div class="photo-grid" id="photo-grid">
        ${Array(6).fill(0).map((_, i) => `
          <div class="photo-slot" data-index="${i}">
            <input type="text" 
              class="photo-tag-input" 
              data-index="${i}" 
              placeholder="Enter tag..." 
              style="display: none; margin-bottom: 5px; padding: 5px; width: 100%; box-sizing: border-box;" />
            ${ newPhotoButton }
            ${ removePhotoButton }
            <div class="photo-preview" data-index="${i}"></div>
            <div class="photo-tag-display" data-index="${i}" style="font-size: 0.8em; margin-top: 3px; color: #666;"></div>
          </div>
        `).join('')}
      </div>
    </div>
    <div class="wizard-left" style="width: 100%; margin-top: 20px;">
      <h3>Drawings</h3>
      <div class="drawing-area" id="drawing-area">
        <div class="drawing-placeholder">
          <p>Click to open drawing canvas (${numberOfItems} items)</p>
        </div>
      </div>
    </div>
  `;
  return container;
}

function createSignoffForm(signoffData?: Wizard6Signoff): HTMLElement {
  const container = document.createElement('div');
  container.className = 'step-card signoff-container';
  
  // Get current total from orderData
  const currentTotal = orderData?.totalContractPrice || 0;
  
  container.innerHTML = `
  <div class="wizard-right">
    <div class="form-group">
      <label for="orderSummary">Order Summary</label>
      <textarea id="orderSummary" name="orderSummary" class="auto" readonly="readonly" rows=30>${signoffData?.orderSummary || ''}</textarea>
    </div>
    <div class="form-group">
      <label for="totalContractPrice">Total Contract Price</label>
      <input type="text" id="totalContractPrice" name="totalContractPrice" readonly="readonly" style="font-size: 1.2em; font-weight: bold;" value="£${currentTotal.toFixed(2)}">
    </div>
  </div>
  <div class="wizard-left">
    <h3>Sales</h3>
    <div class="form-group">
      <label for="salesGroup">Sales Group</label>
      <select 
        id="salesGroup" 
        name="salesGroup" 
        data-orderoptions="sales_group" 
        data-selected-value="${signoffData?.salesGroup || ''}"
        placeholder="Sales Group"
        required>
      </select>
    </div>
  </div>

  <div class="wizard-left">
    <h3>VAT Exemption</h3>
    <div class="form-group">
      <label for="vatExempt">VAT Exempt</label>
      <select id="vatExempt" name="vatExempt">
        <option value="yes" ${signoffData?.vatExempt ? 'selected' : ''}>Yes</option>
        <option value="no" ${!signoffData?.vatExempt ? 'selected' : ''}>No</option>
      </select> 
    </div>

    <div class="form-group">
      <label for="vatPersonal">Personal/Domestic use</label>
      <select id="vatPersonal" name="vatPersonal">
        <option value="yes" ${signoffData?.vatPersonalUseOnly ? 'selected' : ''}>Yes</option>
        <option value="no" ${!signoffData?.vatPersonalUseOnly ? 'selected' : ''}>No</option>
      </select> 
    </div>

    <div class="form-group">
      <label for="vatExemptionReason">Medical Reason</label>
      <select 
        id="vatExemptionReason" 
        name="vatExemptionReason" 
        data-orderoptions="vat_exempt_reason" 
        data-selected-value="${signoffData?.vatExemptionReason || ''}"
        placeholder="VAT Exempt Reason"
        required>
      </select>
    </div>
    <p>Click to see the <button id="termsBtn" class="silent-btn"> Terms and Conditions</button></p>
    <p>CDM (2015) Regulation. By Signing this agreement: I agree to pass on the responsibilities of the above act to The Walk In Bath Co. Ltd/Budget Bathing.</p>
    <div class="form-group">
      <label>Customer Signature</label>
      <div id="signature-preview-area" class="signature-preview-area" style="width: 480px; height: 160px; border: 2px solid #ccc; cursor: pointer; display: flex; align-items: center; justify-content: center; background: #f9f9f9; margin: 0px auto;">
        <span style="color: #999;">Click to add signature</span>
      </div>
      <button type="button" id="delete-signature-btn" class="btn btn-danger" style="display: none; margin-top: 10px;">Delete Signature</button>
    </div>
    <div class="form-group">
      <label for="signedByCustomer">Signed By <span id="dateSigned"></span></label>
      <input type="hidden" id="lastSignedDate" name="lastSignedDate" value="${signoffData?.lastSignedDate || ''}" />
      <input type="text" id="signedByCustomer" name="signedByCustomer" placeholder="Customer name" value="${signoffData?.signedByCustomer || ''}">
    </div>
  </div>
  <div class="wizard-right">
    <div class="form-group">
      <label for="comments">Comments</label>
      <textarea id="comments" name="comments" placeholder="Additional comments" class="auto" rows=30>${signoffData?.comments || ''}</textarea>
    </div>
  </div>
  `;
  return container;
}

// Initialize default step data
function getDefaultStepData(): StepData {
  const defaultAddress = getDefaultAddress();
  return {
    currentStep: 0,
    steps: {
      step1: { number: 1, data: { customerData: { name: '', customerAddress: defaultAddress, installationAddress: defaultAddress, sameAsCustomerAddress: true, phone: '', email: '' } } },
      step2: { number: 2, data: { propertyData: {} as propertyDetails } },
      step3: { 
        number: 3, 
        data: { 
          productType: '', 
          productRange: 0,
          productVariant: 0,
          productScreenOption: 0,
          productTap: '',
          productEndPanel: '',
          productBoxSection: '',
          productShowerTraySize: '',
          productScreenSize: '',
          productScreenSidePanel: '',
          productShowerType: '',
          productSeat: '',
          wasteBooster: '', 
          wasteBoosterRequired: '', 
          electrianRequired: '',
          wastePump: '', 
          wastePumpRequired: '',  
        } 
      },
      step4: { 
        number: 4, 
        data: { 
          wallboardPanelColour: '', 
          wallboardPanelLinearMeters: 0, 
          wallboardPanelTrims: '', 
          wallboardPanelWholeBathroom: '', 
          wallboardPanelAdditionalBoard: '', 
          floorType: '', 
          floorColour: '', 
          ceilingPanelPlusDownlights: false, 
          extractorFan: '', 
          centralCeilingLight: false, 
          additionalDownlighters: '',
          grabRails: '',
          grabRailsReq: '',
          supportRails: '',
          floorToCeilingPole: '',
          heatedTowelRail: '',
          mirrors: '',
          saniflow: '',
          ceilingPanels: '' 
        } 
      },
      step5: { number: 5, data: { photos: [], drawingData: {} as any } },
      step6: { 
        number: 6, 
        data: { 
          signedByCustomer: '', 
          lastSignedDate: '', 
          comments: '', 
          vatExempt: false, 
          vatPersonalUseOnly: false, 
          salesGroup: '', 
          orderSummary: '' 
        } 
      }
    }
  };
}

// Create steps array dynamically from current orderData
async function createStepsArray(): Promise<{ title: string; component: HTMLElement }[]> {
  return [
    { title: 'Customer Details', component: createCustomerForm(orderData.step_data.steps.step1.data) },
    { title: 'Property Details', component: createPropertyForm(orderData.step_data.steps.step2.data) },
    { title: 'Product Details', component: createProductForm(orderData.step_data.steps.step3.data) },
    { title: 'Decoration', component: createDecorationForm(orderData.step_data.steps.step4.data) },
    { title: 'Photos and Drawings', component: createPhotosDrawingForm(orderData.step_data.steps.step5.data) },
    { title: 'Signoff', component: createSignoffForm(orderData.step_data.steps.step6.data) },
  ];
}


export async function initWizard(id: number) {
  orderId = id;
  const storedOrder: Order | null = await getOrderById(id);

  


  //TODO create a clientOptionItems type that stores the cached options for all steps
  //Then when all the steps are rendered we can use these cached options to populate the select options
  //This will avoid multiple DB calls for the same data
  //The selects have data-orderoptions attributes that can be used to map the options to the correct select elements.
  //the field_key in the client_option_items table matches the data-orderoptions attribute value.
  //each select option can then populated using the values. 
  //The values will be used for the select option value and the title can be used for display/text.
  //Use the value if the title is null. 
  //Some items also have prices associated with them and this will be used later for calculating the order total and populating the order summary.

  cachedClientOptions = await getClientOptionsAll();
  //console.log('Cached client options loaded:', cachedClientOptions);

  packsByRange = await loadPacksOffline();

  incompatibilityLookups = await loadIncompatibilitiesOffline()
  
  //console.log(`Loaded order from DB order id ${orderId}:`, storedOrder);
  if (storedOrder) {
    orderData = storedOrder;
    currentStep = orderData.step_data?.currentStep || 0;
  } else {

    console.log('No existing order found');
    navigate('/dashboard');
    return;
    // Create new order with default structure
    // orderData = {
    //   id: id,
    //   orderDate: new Date(),
    //   status: 'draft',
    //   totalContractPrice: 0,
    //   step_data: getDefaultStepData()
    // };
    // await saveOrder(orderData);
  }

  // Ensure step_data has proper structure
  if (!orderData.step_data || !orderData.step_data.steps) {
    orderData.step_data = getDefaultStepData();
  }

  // Rebuild storedPrices from saved order data BEFORE rendering
  // This ensures price selections are restored when reopening an order
  //console.log('[initWizard] Rebuilding price data from saved order...');
  rebuildStoredPricesFromOrderData();

  const steps = await createStepsArray();
  renderStep(steps);
  updateStepProgress();

  // Navigation and save handlers
  document.getElementById('app-content')!.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'BUTTON') {
      
      console.log('app-content...');

      if (target.textContent?.includes(WIZARD_BUTTON_BACK_TEXT) || target.textContent?.includes(WIZARD_BUTTON_PREV_TEXT)) {
        prevStep(); //steps
      } else if (target.textContent?.includes(WIZARD_BUTTON_NEXT_TEXT) || target.textContent?.includes(WIZARD_BUTTON_SAVE_AND_CLOSE_TEXT)) {
        nextStep(steps);
      }
    }
  });

  bindPackValidation();

  document.getElementById('header-content')!.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'BUTTON') {

      console.log('header-content...');
      if (target.classList.contains('wizard-exit')) {
        console.log('Exiting wizard...');
        closeWizard();
      }
    }
  });

}

// Close wizard and return to dashboard
async function closeWizard() {
  // CRITICAL: Capture prices from current step BEFORE saving
  capturePricesFromCurrentStep();
  
  saveCurrentStep();
  
  // Recalculate order summary and total before closing
  updateOrderSummaryAndTotal();
  
  // Check if we're on the last step (Signoff - step 5)
  if (currentStep === 5) {
    const userRole = getUserRole();
    
    if (userRole === SALESREP_ROLE) {

      // Sales user - offer to submit order or save as draft
      const result = await showConfirmationModal({
        title: 'Submit Order?',
        message: 'Would you like to submit this order? Once submitted, the order will be locked for processing and sent for survey review.',
        modalType: 'info',
        buttons: [
          { label: 'Submit Order', value: 'submit', style: 'primary' },
          { label: 'Save as Draft', value: 'draft', style: 'secondary', isDefault: true },
          { label: 'Cancel', value: 'cancel', style: 'secondary' }
        ]
      });
      
      if (result === 'submit') {
        // Check if signature exists before submitting
        const signoffData = orderData.step_data.steps.step6.data;
        if (!signoffData.clientSignature) {
          console.log('[closeWizard] No signature found, prompting user...');
          
          const signatureResult = await showConfirmationModal({
            title: 'Missing Customer Signature',
            message: 'This order does not have a customer signature. Would you like to add one before submitting?',
            modalType: 'warning',
            buttons: [
              { label: 'Add Signature', value: 'add', style: 'primary' },
              { label: 'Submit Anyway', value: 'submit', style: 'danger' },
              { label: 'Cancel', value: 'cancel', style: 'secondary', isDefault: true }
            ]
          });
          
          if (signatureResult === 'add') {
            // Open signature modal
            openSignatureModal();
            console.log('[closeWizard] User chose to add signature, staying in wizard');
            return;
          } else if (signatureResult === 'cancel') {
            // User cancelled - stay in wizard
            console.log('[closeWizard] User cancelled submission due to missing signature');
            return;
          }
          // If 'submit', continue with submission below
        }
        
        orderData.status = ORDER_STATUS_SUBMITTED;
        await saveOrder(orderData);
        console.log('[closeWizard] Order submitted with status:', ORDER_STATUS_SUBMITTED);
      } else if (result === 'draft') {
        // Just save without changing status
        await saveOrder(orderData);
        console.log('[closeWizard] Order saved as draft');
      } else {
        // User cancelled - stay in wizard
        console.log('[closeWizard] User cancelled, staying in wizard');
        return;
      }
    } else if (userRole === SURVEYOR_ROLE) {
      // Surveyor - offer to approve, reject, or save as draft
      const result = await showConfirmationModal({
        title: 'Update Survey Status?',
        message: 'Would you like to update the survey status for this order?',
        modalType: 'info',
        buttons: [
          { label: 'Approve Survey', value: 'approve', style: 'primary' },
          { label: 'Reject Survey', value: 'reject', style: 'danger' },
          { label: 'Save as Draft', value: 'draft', style: 'secondary', isDefault: true },
          { label: 'Cancel', value: 'cancel', style: 'secondary' }
        ]
      });
      
      if (result === 'approve') {
        orderData.status = ORDER_STATUS_APPROVED;
        await saveOrder(orderData);
        console.log('[closeWizard] Survey approved with status:', ORDER_STATUS_APPROVED);
      } else if (result === 'reject') {
        orderData.status = ORDER_STATUS_REJECTED;
        await saveOrder(orderData);
        console.log('[closeWizard] Survey rejected with status:', ORDER_STATUS_REJECTED);
      } else if (result === 'draft') {
        // Just save without changing status
        await saveOrder(orderData);
        console.log('[closeWizard] Order saved as draft');
      } else {
        // User cancelled - stay in wizard
        console.log('[closeWizard] User cancelled, staying in wizard');
        return;
      }
    } else {
      // Unknown role or no role - just save and close
      await saveOrder(orderData);
    }
  } else {
    // Not on last step - just save and close
    await saveOrder(orderData);
  }
  
  // Navigate to dashboard using SPA navigation
  navigate('/dashboard');
}

function initializeCopyAddressIfSameAs(isSameAs: boolean) {

  console.log('initializeCopyAddressIfSameAs', isSameAs);
  if(isSameAs){
    copyAddressFunction();
  }

  const sameAsCheckbox = document.getElementById('sameAs') as HTMLInputElement;
  if (sameAsCheckbox) {
    sameAsCheckbox.addEventListener('change', () => copyAddressFunction());

  }
}


function copyAddressFunction() {
  console.log('Copy address checkbox clicked');

  const custHouseNoField = (document.getElementById('custHouseNo') as HTMLInputElement);
  const custStreetField = (document.getElementById('custStreet') as HTMLInputElement);
  const custCityField = (document.getElementById('custCity') as HTMLInputElement);
  const custCountyField = (document.getElementById('custCounty') as HTMLSelectElement);
  const custCountryField = (document.getElementById('custCountry') as HTMLSelectElement);
  const custPostcodeField = (document.getElementById('custPostcode') as HTMLInputElement);

  const installHouseNo = (document.getElementById('instHouseNo') as HTMLInputElement);
  const installStreet = (document.getElementById('instStreet') as HTMLInputElement);
  const installCity = (document.getElementById('instCity') as HTMLInputElement);
  const installCounty = (document.getElementById('instCounty') as HTMLSelectElement);
  const installCountry = (document.getElementById('instCountry') as HTMLSelectElement);
  const installPostcode = (document.getElementById('instPostcode') as HTMLInputElement);

  const isChecked: boolean = (document.getElementById('sameAs') as HTMLInputElement).checked; //(e !== null && e.target as HTMLInputElement).checked;
  if (isChecked) {

    const custHouseNo = custHouseNoField.value;
    const custStreet = custStreetField.value;
    const custCity = custCityField.value;
    const custCounty = custCountyField.value;
    const custCountry = custCountryField.value;
    const custPostcode = custPostcodeField.value;

    installHouseNo.value = custHouseNo;
    installStreet.value = custStreet;
    installCity.value = custCity;
    installCounty.value = custCounty;
    installCountry.value = custCountry;
    installPostcode.value = custPostcode;

  }
  installHouseNo.readOnly = isChecked;
  installStreet.readOnly = isChecked;
  installCity.readOnly = isChecked;
  installCounty.disabled = isChecked;
  installCountry.disabled = isChecked;
  installPostcode.readOnly = isChecked;

}

/**
 * Update equipment listbox display with current items
 * @param listId - ID of the listbox element
 * @param items - Array of equipment items to display
 * @param arrayType - Type of equipment array (install, remove, keep)
 */
function updateEquipmentList(listId: string, items: string[], arrayType: 'install' | 'remove' | 'keep') {
  const listbox = document.getElementById(listId);
  if (!listbox) {
    console.warn(`[updateEquipmentList] Listbox ${listId} not found`);
    return;
  }
  
  // Clear existing content
  listbox.innerHTML = '';
  
  if (items.length === 0) {
    listbox.innerHTML = '<div style="padding: 10px; color: #999; font-style: italic;">No items added</div>';
    return;
  }
  
  // Create list items
  items.forEach((item, index) => {
    const itemDiv = document.createElement('div');
    itemDiv.className = 'equipment-list-item';
    itemDiv.setAttribute('data-index', index.toString());
    itemDiv.setAttribute('data-array-type', arrayType);
    itemDiv.innerHTML = `
      <span class="equipment-item-text">${item}</span>
      <button type="button" class="equipment-remove-btn" title="Remove">×</button>
    `;
    listbox.appendChild(itemDiv);
  });
  
  console.log(`[updateEquipmentList] Updated ${listId} with ${items.length} items`);
}

/**
 * Initialize equipment controls for add/remove functionality
 */
function initializeEquipmentControls() {
  //console.log('[initializeEquipmentControls] Setting up equipment management controls');
  
  // Helper function to add equipment
  const addEquipment = (selectId: string, listId: string, arrayType: 'install' | 'remove' | 'keep') => {
    const select = document.getElementById(selectId) as HTMLSelectElement;
    if (!select || !select.value || select.value === '') {
      console.warn('[addEquipment] No item selected');
      return;
    }
    
    // Get the selected option text
    const selectedOption = select.options[select.selectedIndex];
    const itemText = selectedOption.textContent || selectedOption.value;
    
    // Get the appropriate array from propertyData
    const propertyData = orderData.step_data.steps.step2.data.propertyData;
    let targetArray: string[];
    
    switch (arrayType) {
      case 'install':
        targetArray = propertyData.equipmentToBeInstalled;
        break;
      case 'remove':
        targetArray = propertyData.equipmentToBeRemoved;
        break;
      case 'keep':
        targetArray = propertyData.equipmentToKeep;
        break;
    }
    
    // Check if item already exists
    if (targetArray.includes(itemText)) {
      console.log(`[addEquipment] Item "${itemText}" already in list`);
      alert('This item is already in the list');
      return;
    }
    
    // Add to array
    targetArray.push(itemText);
    
    // Update display
    updateEquipmentList(listId, targetArray, arrayType);
    
    // Reset select
    select.selectedIndex = 0;
    
    console.log(`[addEquipment] Added "${itemText}" to ${arrayType} list`);
  };
  
  // Add button event listeners for equipment
  const installBtn = document.getElementById('addEquipmentInstallButton');
  const removeBtn = document.getElementById('addEquipmentRemoveButton');
  const keepBtn = document.getElementById('addEquipmentKeepButton');
  const buildingworkBtn = document.getElementById('addBuildingworkButton');
  
  if (installBtn) {
    installBtn.addEventListener('click', () => {
      addEquipment('equipmentToBeInstalled', 'equipmentInstallList', 'install');
    });
  }
  
  if (removeBtn) {
    removeBtn.addEventListener('click', () => {
      addEquipment('equipmentToBeRemoved', 'equipmentRemoveList', 'remove');
    });
  }
  
  if (keepBtn) {
    keepBtn.addEventListener('click', () => {
      addEquipment('equipmentToKeep', 'equipmentKeepList', 'keep');
    });
  }
  
  // Add buildingworks button handler (same logic as equipment - string array)
  if (buildingworkBtn) {
    buildingworkBtn.addEventListener('click', () => {
      const select = document.getElementById('buildingwork') as HTMLSelectElement;
      if (!select || !select.value || select.value === '') {
        console.warn('[addBuildingwork] No item selected');
        return;
      }
      
      // Get the numeric ID from the select value
      const buildingWorkId = parseInt(select.value, 10);
      if (isNaN(buildingWorkId)) {
        console.warn('[addBuildingwork] Invalid accessory ID:', select.value);
        return;
      }

      // Get the array from propertyData
      const propertyData = orderData.step_data.steps.step2.data.propertyData as propertyDetails;
      if (!propertyData.buildingWorks) {
        propertyData.buildingWorks = [];
      }
      
      // Check if accessory already exists
      if (propertyData.buildingWorks.includes(buildingWorkId)) {
        console.log(`[addBuildingwork] BuildingWorks ID ${buildingWorkId} already in list`);
        alert('This building works is already in the list');
        return;
      }
      
      // Add to array
      propertyData.buildingWorks.push(buildingWorkId);
      
      // Update display
      updateBuildingWorksList('buildingworkList', propertyData.buildingWorks);
      
      // Reset select
      select.selectedIndex = 0;
      
      console.log(`[addBuildingwork] Added building work ID ${buildingWorkId} to buildingwork list`);
      
    });
  }
  
  // Remove button event listeners (using event delegation)
  const handleRemoveClick = (e: Event) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('equipment-remove-btn')) {
      const itemDiv = target.closest('.equipment-list-item') as HTMLElement;
      if (!itemDiv) return;
      
      const index = parseInt(itemDiv.getAttribute('data-index') || '0');
      const arrayType = itemDiv.getAttribute('data-array-type') as 'install' | 'remove' | 'keep';
      
      // Get the appropriate array and listbox
      const propertyData = orderData.step_data.steps.step2.data.propertyData;
      let targetArray: string[];
      let listId: string;
      
      switch (arrayType) {
        case 'install':
          targetArray = propertyData.equipmentToBeInstalled;
          listId = 'equipmentInstallList';
          break;
        case 'remove':
          targetArray = propertyData.equipmentToBeRemoved;
          listId = 'equipmentRemoveList';
          break;
        case 'keep':
          targetArray = propertyData.equipmentToKeep;
          listId = 'equipmentKeepList';
          break;
      }
      
      // Remove from array
      const removedItem = targetArray.splice(index, 1);
      console.log(`[handleRemoveClick] Removed "${removedItem}" from ${arrayType} list`);
      
      // Update display
      updateEquipmentList(listId, targetArray, arrayType);
    }
  };
  
  // Attach remove handlers to all four listboxes (3 equipment + buildingWorks)
  const installList = document.getElementById('equipmentInstallList');
  const removeList = document.getElementById('equipmentRemoveList');
  const keepList = document.getElementById('equipmentKeepList');
  const buildingworkList = document.getElementById('buildingworkList');
  
  if (installList) installList.addEventListener('click', handleRemoveClick);
  if (removeList) removeList.addEventListener('click', handleRemoveClick);
  if (keepList) keepList.addEventListener('click', handleRemoveClick);
  if (buildingworkList) buildingworkList.addEventListener('click', handleRemoveClick);
  
  // Initialize displays with existing data
  const propertyData = orderData.step_data.steps.step2.data.propertyData;
  updateEquipmentList('equipmentInstallList', propertyData.equipmentToBeInstalled || [], 'install');
  updateEquipmentList('equipmentRemoveList', propertyData.equipmentToBeRemoved || [], 'remove');
  updateEquipmentList('equipmentKeepList', propertyData.equipmentToKeep || [], 'keep');
  updateBuildingWorksList('buildingworkList', propertyData.buildingWorks || []);
  
  //console.log('[initializeEquipmentControls] Equipment controls initialized');
}


function updateBuildingWorksList(listId: string, buildingWorksIds: number[]) {
  const listbox = document.getElementById(listId);
  if (!listbox) {
    console.warn(`[buildingWorksList] Listbox ${listId} not found`);
    return;
  }
  
  // Clear existing content
  listbox.innerHTML = '';
  
  if (buildingWorksIds.length === 0) {
    listbox.innerHTML = '<div style="padding: 10px; color: #999; font-style: italic;">No building works added</div>';
    return;
  }
  
  // Create list items - lookup text from cachedClientOptions
  buildingWorksIds.forEach((buildingWorkId, index) => {
    // Find the accessory option in cached data to get display text
    const buildingWorkOption = cachedClientOptions?.find(
      item => item.field_key === 'buildingworks' && item.field_value === buildingWorkId.toString()
    );
    
    const displayText = buildingWorkOption?.field_title || buildingWorkOption?.field_value || `BuildingWork ID: ${buildingWorkId}`;
    
    const itemDiv = document.createElement('div');
    itemDiv.className = 'equipment-list-item';
    itemDiv.setAttribute('data-index', index.toString());
    itemDiv.setAttribute('data-id', buildingWorkId.toString());
    itemDiv.innerHTML = `
      <span class="equipment-item-text">${displayText}</span>
      <button type="button" class="equipment-remove-btn" title="Remove">×</button>
    `;
    listbox.appendChild(itemDiv);
  });
  
  console.log(`[buildingWorksList] Updated ${listId} with ${buildingWorksIds.length} building works`);
}

/**
 * Update accessories listbox display with current items (number array)
 * @param listId - ID of the listbox element
 * @param accessoryIds - Array of accessory IDs to display
 */
function updateAccessoriesList(listId: string, accessoryIds: number[]) {
  const listbox = document.getElementById(listId);
  if (!listbox) {
    console.warn(`[updateAccessoriesList] Listbox ${listId} not found`);
    return;
  }
  
  // Clear existing content
  listbox.innerHTML = '';
  
  if (accessoryIds.length === 0) {
    listbox.innerHTML = '<div style="padding: 10px; color: #999; font-style: italic;">No accessories added</div>';
    return;
  }
  
  // Create list items - lookup text from cachedClientOptions
  accessoryIds.forEach((accessoryId, index) => {
    // Find the accessory option in cached data to get display text
    const accessoryOption = cachedClientOptions?.find(
      item => item.field_key === 'accessories' && item.field_value === accessoryId.toString()
    );
    
    const displayText = accessoryOption?.field_title || accessoryOption?.field_value || `Accessory ID: ${accessoryId}`;
    
    const itemDiv = document.createElement('div');
    itemDiv.className = 'equipment-list-item';
    itemDiv.setAttribute('data-index', index.toString());
    itemDiv.setAttribute('data-id', accessoryId.toString());
    itemDiv.innerHTML = `
      <span class="equipment-item-text">${displayText}</span>
      <button type="button" class="equipment-remove-btn" title="Remove">×</button>
    `;
    listbox.appendChild(itemDiv);
  });
  
  console.log(`[updateAccessoriesList] Updated ${listId} with ${accessoryIds.length} accessories`);
}

/**
 * Initialize accessories controls for add/remove functionality (number array)
 */
function initializeAccessoriesControls() {
  console.log('[initializeAccessoriesControls] Setting up accessories management controls');
  
  const addBtn = document.getElementById('addAccessoriesButton');
  const accessoriesList = document.getElementById('accessoriesList');
  
  // Add button event listener
  if (addBtn) {
    addBtn.addEventListener('click', () => {
      const select = document.getElementById('accessories') as HTMLSelectElement;
      if (!select || !select.value || select.value === '') {
        console.warn('[addAccessories] No accessory selected');
        return;
      }
      
      // Get the numeric ID from the select value
      const accessoryId = parseInt(select.value, 10);
      if (isNaN(accessoryId)) {
        console.warn('[addAccessories] Invalid accessory ID:', select.value);
        return;
      }
      
      // Get the accessories array from decorationData
      const decorationData = orderData.step_data.steps.step4.data;
      if (!decorationData.accessories) {
        decorationData.accessories = [];
      }
      
      // Check if accessory already exists
      if (decorationData.accessories.includes(accessoryId)) {
        console.log(`[addAccessories] Accessory ID ${accessoryId} already in list`);
        alert('This accessory is already in the list');
        return;
      }
      
      // Add to array
      decorationData.accessories.push(accessoryId);
      
      // Update display
      updateAccessoriesList('accessoriesList', decorationData.accessories);
      
      // Reset select
      select.selectedIndex = 0;
      
      console.log(`[addAccessories] Added accessory ID ${accessoryId} to accessories list`);
    });
  }
  
  // Remove button event listener (using event delegation)
  if (accessoriesList) {
    accessoriesList.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('equipment-remove-btn')) {
        const itemDiv = target.closest('.equipment-list-item') as HTMLElement;
        if (!itemDiv) return;
        
        const index = parseInt(itemDiv.getAttribute('data-index') || '0');
        const decorationData = orderData.step_data.steps.step4.data;
        
        if (!decorationData.accessories) return;
        
        // Remove from array
        const removedId = decorationData.accessories.splice(index, 1);
        console.log(`[handleAccessoryRemove] Removed accessory ID ${removedId} from list`);
        
        // Update display
        updateAccessoriesList('accessoriesList', decorationData.accessories);
      }
    });
  }
  
  // Initialize display with existing data
  const decorationData = orderData.step_data.steps.step4.data;
  updateAccessoriesList('accessoriesList', decorationData.accessories || []);
  
  console.log('[initializeAccessoriesControls] Accessories controls initialized');
}

/**
 * Update visibility of product form sections based on selected product type
 * - Bath: Show Bath section only
 * - Shower: Show Shower section only
 * - Wetroom | Walk-In: Show both Bath and Shower sections
 * - Empty/default: Hide both sections
 */
function updateProductFormVisibility() {
  const productTypeSelect = document.getElementById('productType') as HTMLSelectElement;
  const bathSection = document.getElementById('product-details-bath') as HTMLElement;
  const showerSection = document.getElementById('product-details-shower') as HTMLElement;
  
  if (!productTypeSelect || !bathSection || !showerSection) {
    console.warn('[updateProductFormVisibility] Required elements not found');
    return;
  }
  
  const productType = productTypeSelect.value.toLowerCase();
  //console.log(`[updateProductFormVisibility] Product type selected: "${productType}"`);
  
  // Default: hide both sections
  bathSection.style.display = 'none';
  showerSection.style.display = 'none';
  
  // Show sections based on product type
  if (productType === 'bath') {
    bathSection.style.display = 'block';
  } else if (productType === 'shower') {
    showerSection.style.display = 'block';
  } else if (productType === 'wetroom' || productType === 'walk-in') {
    bathSection.style.display = 'block';
    showerSection.style.display = 'block';
  }
  
  //console.log(`[updateProductFormVisibility] Bath section: ${bathSection.style.display}, Shower section: ${showerSection.style.display}`);
}


/**
 * Populate select options from cached client options data OR local JSON files
 * Finds all select elements with data-orderoptions attribute and populates them
 * with options from either:
 * - Database cache (default)
 * - Local JSON file (when data-orderlocal="true")
 */
async function populateSelectOptionsFromCache() {
  // Find all select elements with data-orderoptions attribute
  const selectElements = document.querySelectorAll('select[data-orderoptions]');
  
  selectElements.forEach(async (selectElement) => {
    const select = selectElement as HTMLSelectElement;
    const fieldKey = select.getAttribute('data-orderoptions');
    const isLocalResource = select.getAttribute('data-orderlocal') === 'true';
    
    if (!fieldKey) {
      return;
    }
    
    // Get the intended selected value from data-selected-value attribute
    const selectedValue = select.dataset.selectedValue || '';
    
    // Handle local JSON resources
    if (isLocalResource) {
      try {
        const response = await fetch(`/assets/${fieldKey}.json`);
        const localData = await response.json();
        
        // Clear existing options except the first
        while (select.options.length > 1) {
          select.remove(1);
        }
        
        // Populate options from local JSON (array of strings)
        localData.forEach((value: string) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          select.appendChild(option);
        });
        
        // Restore selected value AFTER options are populated
        if (selectedValue && selectedValue !== '') {
          select.value = selectedValue;
          //console.log(`[populateSelectOptionsFromCache] Local resource "${fieldKey}" - Restored selection: "${selectedValue}"`);
        }
      } catch (error) {
        console.error(`Failed to load local resource for ${fieldKey}:`, error);
      }
      return;
    }
    
    // Handle database-backed options (existing behavior)
    if (!cachedClientOptions || cachedClientOptions.length === 0) {
      console.warn('No cached client options available to populate selects');
      return;
    }
    
    // Filter options for this specific field key
    const options = cachedClientOptions!.filter(item => item.field_key === fieldKey);
    
    if (options.length === 0) {
      console.warn(`No options found for field key: ${fieldKey}`);
      return;
    }
    
    // Keep the first "Select..." option, clear the rest
    while (select.options.length > 1) {
      select.remove(1);
    }
    
    // Add options from cache
    options.forEach((item) => {
      const option = document.createElement('option');
      option.value = item.field_value;
      // Use title if available, otherwise use value
      option.textContent = item.field_title || item.field_value;
      // Add price as data attribute if available (for future use)
      if (item.price !== null) {
        option.setAttribute('data-price', item.price.toString());
      }
      select.appendChild(option);
    });
    
    // NOW restore the previously selected value (after options are added)
    if (selectedValue && selectedValue !== '') {
      select.value = selectedValue;
      //console.log(`[populateSelectOptionsFromCache] DB resource "${fieldKey}" - Restored selection: "${selectedValue}"`);
    }
  });
}

function renderStep(steps: { title: string; component: HTMLElement }[]) {
  const stepContainer = document.getElementById('step-container')!;
  stepContainer.innerHTML = `
    ${steps[currentStep].component.outerHTML}
    <div class="form-actions" style="clear: both; padding-top: 20px;">
      <button class="btn btn-secondary" ${currentStep === 0 ? 'disabled' : ''} style="background-color: #6c757d;">${WIZARD_BUTTON_BACK_TEXT}</button>
      <button class="btn btn-primary" style="background-color: #1876BD;">${currentStep === 5 ? WIZARD_BUTTON_SAVE_AND_CLOSE_TEXT : WIZARD_BUTTON_NEXT_TEXT}</button>
    </div>
  `;

  // Populate select options from cached data after DOM is updated
  setTimeout(() => {
    populateSelectOptionsFromCache();
    
  }, 50);

  if (currentStep === 0) {
    //console.log('Initializing customer things...');
    setTimeout(() => {
      //console.log('initializeCopyAddressIfSameAs');
      initializeCopyAddressIfSameAs(orderData.step_data.steps.step1.data.customerData.sameAsCustomerAddress);
    }, 100);
  }

  // Initialize equipment controls for step 1 (Property)
  if (currentStep === 1) {
    setTimeout(() => {
      console.log('Initializing equipment controls...');
      
      // Ensure arrays exist (initialize if needed)
      const propertyData = orderData.step_data.steps.step2.data.propertyData;
      if (!propertyData.equipmentToBeInstalled) propertyData.equipmentToBeInstalled = [];
      if (!propertyData.equipmentToBeRemoved) propertyData.equipmentToBeRemoved = [];
      if (!propertyData.equipmentToKeep) propertyData.equipmentToKeep = [];
      
      initializeEquipmentControls();
    }, 100);
  }

  // Initialize product form visibility for step 2 (Products)
  if (currentStep === 2) {
    setTimeout(async () => {
      console.log('Initializing product form visibility...');
      
      // Set initial visibility based on saved product type
      updateProductFormVisibility();
      
      // Add change event listener to productType select
      const productTypeSelect = document.getElementById('productType') as HTMLSelectElement;
      if (productTypeSelect) {
        productTypeSelect.addEventListener('change', async () => {
          updateProductFormVisibility();

          // filter the selection for product ranges based on product type.
          // This will work just like the variant filtering. The filtering var variants used range as parent id. 
          // This will use product type as parent id and range as child id. 

          const selectedType = productTypeSelect.value;
          const productRangeSelect = document.getElementById('productRange') as HTMLSelectElement;
          
          // Get all range options from cached client options
          const allRangeOptions = cachedClientOptions?.filter(item => item.field_key === 'product_range') || [];
          
          const childrangeIds = await getCatalogueChildren("product_range", selectedType);

          

          // Clear productRange select (keep first "Select..." option)
          while (productRangeSelect.options.length > 1) {
            productRangeSelect.remove(1);
          }

          let matchingChildrenCount = 0;
          // Filter and add only ranges that match the selected product type
          allRangeOptions.forEach((item) => {
            const rangeId: string = item.field_value || '';
            let foundMatch = false;
            for (let i = 0; i < childrangeIds.length; i++) {
              if (rangeId.toString() === childrangeIds[i].toString()) {
                foundMatch = true;
                break;
              }
            }

            if (foundMatch === true) { // childrangeIds.includes(rangeId)
              matchingChildrenCount++;

              const option = document.createElement('option');
              option.value = item.field_value;
              option.textContent = item.field_title || item.field_value;
              if (item.price !== null) {
                option.setAttribute('data-price', item.price.toString());
              }
              productRangeSelect.appendChild(option);
            }
          });
          console.log(`[productType change] Filtered ptyppes ${matchingChildrenCount}/${allRangeOptions.length - 1} options for type "${selectedType}"`);
          


          // Also trigger change event on productVariant to re-filter variants vased on new range

        });
      }
      



      // Add change event listener to productRange select for filtering variants
      
      const productRangeSelect = document.getElementById('productRange') as HTMLSelectElement;
      const productVariantSelect = document.getElementById('productVariant') as HTMLSelectElement;
      
      if (productRangeSelect && productVariantSelect) {
        productRangeSelect.addEventListener('change', async () => {

          

          const selectedRange = productRangeSelect.value;
          console.log(`[productRange change] Selected range: ${selectedRange}`);
          
          if (!selectedRange || selectedRange === '') {
            //console.log('[productRange change] No range selected, skipping variant filter');
            return;
          }

          

          
          // SAVE the intended variant value BEFORE filtering
          const intendedVariantValue = productVariantSelect.dataset.selectedValue || '';
          //console.log(`[productRange change] Saving intended variant value: ${intendedVariantValue}`);
          
          // Fetch catalogue children for this parent range
          const childVariantIds = await getCatalogueChildren("product_variant", selectedRange);
          //console.log(`[productRange change] Found ${childVariantIds.length} child variants for range ${selectedRange}:`, childVariantIds);
          
          // Get all variant options from cached client options
          const allVariantOptions = cachedClientOptions?.filter(item => item.field_key === 'product_variant') || [];
          
          // Clear variant select (keep first "Select..." option)
          while (productVariantSelect.options.length > 1) {
            productVariantSelect.remove(1);
          }
          
          // Filter and add only variants that are children of the selected range
          allVariantOptions.forEach((item) => {
            const variantId: string = item.field_value;
            
            // Only add this variant if it's in the catalogue children list
            let foundVMatch = false;
            for (let i = 0; i < childVariantIds.length; i++) {
              if (variantId.toString() === childVariantIds[i].toString()) {
                foundVMatch = true;
                break;
              }
            }

            if (foundVMatch) { //childVariantIds.includes(variantId)) {
              const option = document.createElement('option');
              option.value = item.field_value;
              option.textContent = item.field_title || item.field_value;
              if (item.price !== null) {
                option.setAttribute('data-price', item.price.toString());
              }
              productVariantSelect.appendChild(option);
            }
          });
          
          //console.log(`[productRange change] Filtered variant select to ${productVariantSelect.options.length - 1} options`);
          
          // RESTORE the saved variant value AFTER filtering (if it exists in the filtered list)
          if (intendedVariantValue && intendedVariantValue !== '' && intendedVariantValue !== '0') {
            productVariantSelect.value = intendedVariantValue;
            //console.log(`[productRange change] Restored variant selection: ${intendedVariantValue}`);
          }


          //Range has changed. Clear validations.
          resetClearValidationErrors();
          clearPackValidation();
          clearPackSummary();

          //Write the expected options.
          const packIdx: number = packsByRange.findIndex(x => x.rangeId === parseInt(selectedRange, 10));
          if(packIdx > -1) {
            const currentPack: ProductPackCache = packsByRange[packIdx];


            const requiredPacks: string[] = currentPack.categories
              .filter(pc => pc.manditory)
              .map(pc => formatPackLabel(pc.packKey));

            const optionalPacks: string[] | undefined = currentPack.categories
              .filter(pc => !pc.manditory)
              .map(pc => formatPackLabel(pc.packKey));

            let pSumHtml: string = "";
            
            if(requiredPacks.length > 0){
              pSumHtml += `<span class="success-text">Required:</span> ${requiredPacks.join(', ')}`;
              pSumHtml += `<br />`;
            }

            if(optionalPacks.length > 0){
              pSumHtml += `<span class="secondary-text">Optional:</span> ${optionalPacks.join(', ')}`;
            }

            const pSumContainer = document.getElementById("pack-summary")!;
            pSumContainer.innerHTML = pSumHtml;

          }


          bindPackValidation();



        });
        
        // If there's already a range selected, trigger the filter
        if (productRangeSelect.value && productRangeSelect.value !== '') {
          console.log('[Product form init] Triggering initial range filter');
          productRangeSelect.dispatchEvent(new Event('change'));
        }
      }
    }, 100);
  }

  // Initialize accessories controls for step 3 (Decorations)
  if (currentStep === 3) {
    setTimeout(() => {
      console.log('Initializing accessories controls...');
      
      // Ensure accessories array exists (initialize if needed)
      const decorationData = orderData.step_data.steps.step4.data;
      if (!decorationData.accessories) decorationData.accessories = [];
      
      initializeAccessoriesControls();
    }, 100);
  }

  // Initialize special components for step 4 (Photos/Drawings) and 5 (Signoff)
  if (currentStep === 4) {
    setTimeout(async () => {

      //let propData = orderData.step_data.steps.step2.data.propertyData;
      await initializePhotosAndDrawings();
    }, 100);
  }

  if (currentStep === 5) {
    setTimeout(async () => {
      try {
        // Initialize signature preview area
        await initializeSignaturePreview();
        await intializeTandCs();

        updateSignedDate();
      } catch (error) {
        console.error('Error initializing signature preview:', error);
        // Continue anyway - the form can still be used without the signature
      }
    }, 100);
  }
}

function saveCurrentStep() {
  //console.log(`[saveCurrentStep] Saving step ${currentStep} (${stepTitles[currentStep]})`);
  
  // For step 4 (Photos/Drawings) and step 5 (Signoff), we don't have traditional forms
  // Step 4: Photos and drawings saved via separate handlers
  // Step 5: Signoff uses a div container, not a form element. Data captured by capturePricesFromCurrentStep()
  if (currentStep === 4 || currentStep === 5) {
    orderData.step_data.currentStep = currentStep;
    //console.log(`[saveCurrentStep] Step ${currentStep} - Special handling (no FormData needed)`);
    saveOrder(orderData);
    return;
  }


  const form = document.querySelector('.step-card') as HTMLFormElement;
  if (!form) {
    //console.warn('[saveCurrentStep] No form found for current step');
    return;
  }

  const formData = new FormData(form);

  // Save data based on current step
  switch (currentStep) {
    case 0: // Customer
      // Read checkbox state first
      const sameAsChecked = (document.getElementById('sameAs') as HTMLInputElement)?.checked || false;
      
      // Build customer address
      const custAddress = {
        houseNo: formData.get('custHouseNo') as string || '',
        street: formData.get('custStreet') as string || '',
        city: formData.get('custCity') as string || '',
        county: formData.get('custCounty') as string || '',
        country: formData.get('custCountry') as string || '',
        postcode: formData.get('custPostcode') as string || ''
      };
      
      // If "Same as Customer Address" is checked, copy customer address to installation address
      // Otherwise, read installation address from form fields
      const instAddress = sameAsChecked ? { ...custAddress } : {
        houseNo: formData.get('instHouseNo') as string || '',
        street: formData.get('instStreet') as string || '',
        city: formData.get('instCity') as string || '',
        county: formData.get('instCounty') as string || '',
        country: formData.get('instCountry') as string || '',
        postcode: formData.get('instPostcode') as string || ''
      };
      
      const customerData: Wizard1Customer = {
        customerData: {
          name: formData.get('customerName') as string || '',
          customerAddress: custAddress,
          installationAddress: instAddress,
          sameAsCustomerAddress: sameAsChecked,
          phone: formData.get('custPhone') as string || '',
          email: formData.get('custEmail') as string || ''
        }
      };
      orderData.step_data.steps.step1.data = customerData;
      //console.log('[saveCurrentStep] Step 0 - Customer data saved:', customerData);
      break;

    case 1: // Property
      // Preserve existing equipment arrays (they're managed by the UI controls)
      const existingPropertyData = orderData.step_data.steps.step2.data.propertyData;
      
      const propertyData: Wizard2Property = {
        propertyData: {
          propertyType: formData.get('propertyType') as string || '',
          materialStorageLocation: formData.get('materialStorageLocation') as string || '',
          rubbishLocation: formData.get('rubbishLocation') as string || '',
          permissionRequired: formData.get('permissionRequired') === 'yes',
          parkingPermitRequired: formData.get('parkingPermitRequired') === 'yes',
          stairlift: false,
          floorConstruction: formData.get('floorConstruction') as string || '',
          ceilingHeight: parseFloat(formData.get('ceilingHeight') as string) || 0,
          wallboardTerminationHeight: parseFloat(formData.get('wallboardTerminationHeight') as string) || 0,
          wallboardToCoving: false,
          heatingSystem: formData.get('heatingSystem') as string || '',
          boilerType: formData.get('boilerType') as string || '',
          boilerModel: formData.get('boilerModel') as string || '',
          cylinderCapacity: parseFloat(formData.get('cylinderCapacity') as string) || 0,
          hotWaterFlowRate: parseFloat(formData.get('hotWaterFlowRate') as string) || 0,
          hotTankSizeW: parseFloat(formData.get('hotTankSizeW') as string) || 0,
          hotTankSizeH: parseFloat(formData.get('hotTankSizeH') as string) || 0,
          equipmentToBeInstalled: existingPropertyData?.equipmentToBeInstalled || [],
          equipmentToBeRemoved: existingPropertyData?.equipmentToBeRemoved || [],
          equipmentToKeep: existingPropertyData?.equipmentToKeep || [],
          buildingWorks: existingPropertyData?.buildingWorks || [],
          roomDiamentionW: existingPropertyData.roomDiamentionW,
          roomDiamentionH: existingPropertyData.roomDiamentionH, 
          vanityUnit: formData.get('vanityUnit') as string || '',
          wcUnit: formData.get('wcUnit') as string || '',
          cloakroomPack: formData.get('cloakroomPack') as string || '',
          bidet: formData.get('bidet') as string || '',
          newPosition: formData.get('newPosition') as string || '',
          basinTap: formData.get('basinTap') as string || '',
        }
      };
      orderData.step_data.steps.step2.data = propertyData;
      //console.log('[saveCurrentStep] Step 1 - Property data saved:', propertyData);
      break;

    case 2: // Products
      const productData: Wizard3Products = {
        productType: formData.get('productType') as string || '',
        productRange: parseFloat(formData.get('productRange') as string) || 0,
        productVariant: parseFloat(formData.get('productVariant') as string) || 0,
        productScreenOption: parseFloat(formData.get('productScreenOption') as string) || 0,
        productTap: formData.get('productTap') as string || '',
        productEndPanel: formData.get('productEndPanel') as string || '',
        productBoxSection: formData.get('productBoxSection') as string || '',
        productShowerTraySize: formData.get('productShowerTraySize') as string || '',
        productScreenSize: formData.get('productScreenSize') as string || '',
        productScreenSidePanel: formData.get('productScreenSidePanel') as string || '',
        productShowerType: formData.get('productShowerType') as string || '',
        productSeat: formData.get('productSeat') as string || '',
        wasteBooster: formData.get('wasteBooster') as string || '',
        wasteBoosterRequired: formData.get('wasteBoosterRequired') as string || '',
        electrianRequired: formData.get('electrianRequired') as string || '',
        wastePump: formData.get('wastePump') as string || '',
        wastePumpRequired: formData.get('wastePumpRequired') as string || '',
      };
      orderData.step_data.steps.step3.data = productData;
      //console.log('[saveCurrentStep] Step 2 - Product data saved:', productData);
      break;

    case 3: // Decorations
      // Preserve existing accessories array (it's managed by the UI controls)
      const existingDecorationData = orderData.step_data.steps.step4.data;
      
      const decorationData: Wizard4Decorations = {
        wallboardPanelColour: formData.get('wallboardPanelColour') as string || '',
        wallboardPanelLinearMeters: parseFloat(formData.get('wallboardPanelLinearMeters') as string) || 0,
        wallboardPanelTrims: formData.get('wallboardPanelTrims') as string || '',
        wallboardPanelWholeBathroom: formData.get('wallboardPanelWholeBathroom') as string || '',
        wallboardPanelAdditionalBoard: formData.get('wallboardPanelAdditionalBoard') as string || '',
        floorType: formData.get('floorType') as string || '',
        floorColour: formData.get('floorColour') as string || '',
        ceilingPanelPlusDownlights: formData.get('ceilingPanelPlusDownlights') === 'yes',
        extractorFan: formData.get('extractorFan') as string || '',
        centralCeilingLight: formData.get('centralCeilingLight') === 'yes',
        additionalDownlighters: formData.get('additionalDownlighters') as string || '',
        accessories: existingDecorationData?.accessories || [],
        grabRails: formData.get('grabRails') as string || '',
        grabRailsReq: formData.get('grabRailsReq') as string || '',
        supportRails: formData.get('supportRails') as string || '',
        floorToCeilingPole: formData.get('floorToCeilingPole') as string || '',
        heatedTowelRail: formData.get('heatedTowelRail') as string || '',
        mirrors: formData.get('mirrors') as string || '',
        saniflow: formData.get('saniflow') as string || '',
        ceilingPanels: formData.get('ceilingPanels') as string || ''
      };
      orderData.step_data.steps.step4.data = decorationData;
      //console.log('[saveCurrentStep] Step 3 - Decoration data saved:', decorationData);
      break;

    case 4: // Photos/Drawings
      // Photos and drawings are saved via separate handlers
      break;

    case 5: // Signoff
      const signoffData: Wizard6Signoff = {
        signedByCustomer: formData.get('signedByCustomer') as string || '',
        lastSignedDate: formData.get('lastSignedDate') as string || '',
        comments: formData.get('comments') as string || '',
        vatExempt: formData.get('vatExempt') === 'yes',
        vatExemptionReason: formData.get('vatExemptionReason') as string || '',
        vatPersonalUseOnly: formData.get('vatPersonal') === 'yes',
        salesGroup: formData.get('salesGroup') as string || '',
        orderSummary: ''
      };
      orderData.step_data.steps.step6.data = signoffData;
      //console.log('[saveCurrentStep] Step 5 - Signoff data saved:', signoffData);
      break;
  }

  orderData.step_data.currentStep = currentStep;
  //console.log('[saveCurrentStep] Persisting to database...');
  saveOrder(orderData);
}




async function initializePhotosAndDrawings() {
  console.log('[initializePhotosAndDrawings] Initializing photos and drawings for order', orderId);
  
  // Load existing photos from database
  await loadExistingPhotos();
  
  // Initialize photo capture buttons
  const photoGrid = document.getElementById('photo-grid');
  if (photoGrid) {
    photoGrid.addEventListener('click', async (e) => {
      const target = e.target as HTMLElement;
      
      if (target.classList.contains('add-photo-btn')) {
        //const slotIndex = parseInt(target.closest('.photo-slot')?.getAttribute('data-index') || '0');
        
        try {
          // Prompt for tag
          const tag = prompt('Enter a tag for this photo (e.g., "Before", "Installation", "Details"):', 'Photo');
          if (!tag) {
            console.log('[initializePhotosAndDrawings] Photo capture cancelled - no tag provided');
            return;
          }
          
          const imageCapture = new ImageCapture();
          const file = await imageCapture.capture();
          
          // User cancelled camera
          if (!file) {
            console.log('[initializePhotosAndDrawings] Photo capture cancelled by user');
            return;
          }
          
          // Note: ImageCapture now handles compression internally (HD size limit)
          // Save to database with tag
          await saveImage(orderId, 5, file, tag);
          
          console.log('[initializePhotosAndDrawings] Photo saved, reloading photos from database');
          
          // Reload all photos from database to get the new image ID
          await loadExistingPhotos();
          
        } catch (error) {
          console.error('[initializePhotosAndDrawings] Error capturing photo:', error);
          alert('Failed to capture photo. Please try again.');
        }
      } else if (target.classList.contains('remove-photo-btn')) {
        const slotIndex = parseInt(target.closest('.photo-slot')?.getAttribute('data-index') || '0');
        const photoData = photoSlots[slotIndex];
        
        if (photoData && photoData.id) {
          try {
            // Confirm deletion
            const confirmed = confirm('Are you sure you want to delete this photo?');
            if (!confirmed) {
              return;
            }
            
            console.log(`[initializePhotosAndDrawings] Deleting photo ID ${photoData.id} from database`);
            
            // Delete from database
            await deleteImage(photoData.id);
            
            // Revoke blob URL to free memory
            URL.revokeObjectURL(photoData.blobUrl);
            
            // Clear slot
            photoSlots[slotIndex] = null;
            
            // Reload photos from database
            await loadExistingPhotos();
            
          } catch (error) {
            console.error('[initializePhotosAndDrawings] Error deleting photo:', error);
            alert('Failed to delete photo. Please try again.');
          }
        }
      }
    });
  }
  
  // Initialize drawing area click handler
  const drawingArea = document.getElementById('drawing-area');
  if (drawingArea) {
    drawingArea.addEventListener('click', () => {

      const rPD: propertyDetails = orderData.step_data.steps.step2.data.propertyData;
      openDrawingModal(rPD.roomDiamentionW, rPD.roomDiamentionH);
    });
  }
}

/**
 * Load existing photos from database and display them in the grid
 */
async function loadExistingPhotos() {
  console.log('[loadExistingPhotos] Loading photos for order', orderId);
  
  try {
    // Get images from database
    const images = await getImagesByOrderAndStep(orderId, 5);
    
    if (!images || images.length === 0) {
      console.log('[loadExistingPhotos] No existing photos found');
      
      // Reset slots and show first add button
      photoSlots = [null, null, null, null, null, null];
      updatePhotoGrid();
      return;
    }
    
    console.log(`[loadExistingPhotos] Found ${images.length} photos`);
    
    // Reset photo slots
    photoSlots = [null, null, null, null, null, null];
    
    // Convert blob data to displayable URLs and populate slots
    images.forEach((imageData, index) => {
      if (index < 6) {
        // Create blob URL from Uint8Array - use slice to create a proper copy
        const blobData = imageData.blob.slice(0);
        const blob = new Blob([blobData], { type: 'image/jpeg' });
        const blobUrl = URL.createObjectURL(blob);
        
        photoSlots[index] = {
          id: imageData.id,
          blobUrl: blobUrl,
          tag: imageData.tag || 'Untagged'
        };
        
        console.log(`[loadExistingPhotos] Loaded photo ${index + 1}: ID ${imageData.id}, tag: ${imageData.tag}, ${imageData.filename}`);
      }
    });
    
    // Update the grid display
    updatePhotoGrid();
    
  } catch (error) {
    console.error('[loadExistingPhotos] Error loading photos:', error);
  }
}

/**
 * Update the photo grid display based on current photoSlots state
 */
function updatePhotoGrid() {
  console.log('[updatePhotoGrid] Updating grid display');
  
  for (let i = 0; i < 6; i++) {
    const slot = document.querySelector(`.photo-slot[data-index="${i}"]`);
    if (!slot) continue;
    
    const preview = slot.querySelector('.photo-preview') as HTMLElement;
    const addBtn = slot.querySelector('.add-photo-btn') as HTMLElement;
    const removeBtn = slot.querySelector('.remove-photo-btn') as HTMLElement;
    
    if (!preview) continue;
    
    const photoData = photoSlots[i];
    
    if (photoData) {
      // Slot has a photo - display it
      preview.innerHTML = `<img src="${photoData.blobUrl}" alt="Photo ${i + 1}">`;
      
      if (addBtn) addBtn.style.display = 'none';
      if (removeBtn) removeBtn.style.display = 'flex';
      
    } else {
      // Slot is empty
      preview.innerHTML = '';
      
      // Show add button only for the first empty slot
      const isFirstEmpty = photoSlots.slice(0, i).every(slot => slot !== null);
      
      if (addBtn) addBtn.style.display = isFirstEmpty || i === 0 ? 'flex' : 'none';
      if (removeBtn) removeBtn.style.display = 'none';
    }
  }
  
  console.log('[updatePhotoGrid] Grid updated. Photos loaded:', photoSlots.filter(s => s !== null).length);
}

// Open fullscreen drawing modal
function openDrawingModal(roomDiamentionWidth: number, roomDiamentionHeight: number) {
  //<canvas id="fullscreen-drawing-canvas" width="800" height="600"></canvas>

  const modal = document.createElement('div');
  modal.className = 'fullscreen-modal';
  modal.innerHTML = `
    <div class="fullscreen-modal-header">
      <h3>Drawing Canvas</h3>
      <button class="fullscreen-modal-close">×</button>
    </div>
    <div class="fullscreen-modal-content">
      <div class="drawing-fullscreen-area">
        <div class="controls">
            <label class="drawing-room-label">Room Width (m): <input type="number" id="roomDiamentionW" name="roomDiamentionW" value="${roomDiamentionWidth || '3'}" /> </label>
            <label class="drawing-room-label">Room Height (m): <input type="number" id="roomDiamentionH" name="roomDiamentionH" value="${roomDiamentionHeight || '2.4'}" /> </label>
            <button id="updateRoomBtn">Update Room</button>
        </div>
        <div class="control-mode"><label>Mode: <input type="text" id="controlmode" value=""></label></div>
        <div class="floating-toolbar">
            <button id="moveMode">Move</button>
            <button id="resizeMode">Resize</button>
            <button id="rotateMode">Rotate</button>
            <button id="removeItem">Remove</button>

        </div>
        <div class="toolbar">
            <button id="doorBtn">Door</button>
            <button id="windowBtn">Window</button>
            <button id="bathBtn">Bath</button>
            <button id="sinkBtn">Sink</button>
        </div>
        <div class="property-panel">
            <h3>Properties</h3>
            <label>X Position: <input type="number" id="propX" value="0"></label>
            <label>Y Position: <input type="number" id="propY" value="0"></label>
            <label>Width: <input type="number" id="propWidth" value="0"></label>
            <label>Height: <input type="number" id="propHeight" value="0"></label>
            <label>Rotation: <input type="number" id="propRotation" value="0"></label>
            <button id="updateProperties">Apply Changes</button>
        </div>

        <svg id="roomCanvas" width="500" height="500"></svg>
      </div>
    </div>
  `;
  
  document.body.appendChild(modal);
  
  // Add event listeners for toolbar buttons
  const doorBtn = modal.querySelector('#doorBtn');
  const windowBtn = modal.querySelector('#windowBtn');
  const bathBtn = modal.querySelector('#bathBtn');
  const sinkBtn = modal.querySelector('#sinkBtn');
  const updateRoomBtn = modal.querySelector('#updateRoomBtn');
  const moveModeBtn = modal.querySelector('#moveMode');
  const resizeModeBtn = modal.querySelector('#resizeMode');
  const rotateModeBtn = modal.querySelector('#rotateMode');
  const removeItemBtn = modal.querySelector('#removeItem');
  const updatePropertiesBtn = modal.querySelector('#updateProperties');
  
  if (doorBtn) doorBtn.addEventListener('click', () => selectObject('door'));
  if (windowBtn) windowBtn.addEventListener('click', () => selectObject('window'));
  if (bathBtn) bathBtn.addEventListener('click', async () => {
    // Resolve the product range title from the step 3 productRange select
    const productRangeSelect = document.getElementById('productRange') as HTMLSelectElement | null;
    const selectedOption = productRangeSelect?.options[productRangeSelect.selectedIndex];
    const rangeTitle = selectedOption?.textContent?.trim() || '';

    if (rangeTitle && rangeTitle !== '' && rangeTitle !== 'Select Range') {
      // Normalise title to a filename: lowercase, spaces → underscores
      const svgFilename = rangeTitle.toLowerCase().replace(/\s+/g, '_') + '.svg';
      const svgPath = `/assets/products/${svgFilename}`;

      try {
        const response = await fetch(svgPath);
        if (response.ok) {
          selectedProductSvgContent = await response.text();
          selectedProductSvgSrc = svgPath;
          console.log(`[bathBtn] Loaded product SVG: ${svgPath}`);
        } else {
          console.warn(`[bathBtn] SVG not found at ${svgPath}, using default bath shape`);
          selectedProductSvgContent = null;
          selectedProductSvgSrc = null;
        }
      } catch (err) {
        console.warn(`[bathBtn] Failed to fetch SVG: ${err}`);
        selectedProductSvgContent = null;
        selectedProductSvgSrc = null;
      }
    } else {
      console.warn('[bathBtn] No product range selected, using default bath shape');
      selectedProductSvgContent = null;
      selectedProductSvgSrc = null;
    }

    selectObject('bath');
  });
  if (sinkBtn) sinkBtn.addEventListener('click', () => selectObject('sink'));
  if (updateRoomBtn) updateRoomBtn.addEventListener('click', () => updateRoom());
  if (moveModeBtn) moveModeBtn.addEventListener('click', () => setMode('move'));
  if (resizeModeBtn) resizeModeBtn.addEventListener('click', () => setMode('resize'));
  if (rotateModeBtn) rotateModeBtn.addEventListener('click', () => setMode('rotate'));
  if (removeItemBtn) removeItemBtn.addEventListener('click', () => removeSelectedObject());
  if (updatePropertiesBtn) updatePropertiesBtn.addEventListener('click', () => applyPropertyChanges());
  
  // Initialize the room
  updateRoom();
  
  // Initialize mode display (default is "move")
  setTimeout(() => {
    updateMode();
  }, 50);
  
  // Load existing drawing data after room is set up
  setTimeout(() => {
    loadDrawingData(roomDiamentionWidth, roomDiamentionHeight);
  }, 100);
  
  // Initialize drawing canvas
  const canvas = modal.querySelector('#roomCanvas') as HTMLCanvasElement;
  if (canvas) {
    //const drawingCanvas = new DrawingCanvas(canvas);
    
    // Close button handler
    const closeBtn = modal.querySelector('.fullscreen-modal-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', async () => {
        console.log('Closing drawing modal');

        // Save drawing data before closing
        await saveDrawingData();

        // Update drawing area preview
        const drawingArea = document.getElementById('drawing-area');
        if (drawingArea) {
          const placeholder = drawingArea.querySelector('.drawing-placeholder');
          if (placeholder) {
            const objectCount = orderData.step_data.steps.step5.data.drawingData?.objects2d?.length || 0;
            if (objectCount > 0) {
              placeholder.innerHTML = `<p>Drawing saved (${objectCount} object${objectCount !== 1 ? 's' : ''}) - click to edit</p>`;
            } else {
              placeholder.innerHTML = '<p>Click to open drawing canvas</p>';
            }
          }
        }
        document.body.removeChild(modal);
      });
    }
    
    // Close on background click
    modal.addEventListener('click', (e) => {
      console.log('modal event listener click', e.target);

      if (e.target === modal) {
        const closeBtn = modal.querySelector('.fullscreen-modal-close') as HTMLElement;
        if (closeBtn) {
          closeBtn.click();
        }
      }
    });
  }
}


function updateMode() {
    const modeInput = document.getElementById('controlmode') as HTMLInputElement;
    if (modeInput) {
        modeInput.value = mode.toUpperCase();
    }
    
    // Update toolbar button states
    const moveBtn = document.getElementById('moveMode');
    const resizeBtn = document.getElementById('resizeMode');
    const rotateBtn = document.getElementById('rotateMode');
    
    // Remove active class from all buttons
    [moveBtn, resizeBtn, rotateBtn].forEach(btn => {
        btn?.classList.remove('active-mode');
    });
    
    // Add active class to current mode button
    if (mode === 'move' && moveBtn) moveBtn.classList.add('active-mode');
    if (mode === 'resize' && resizeBtn) resizeBtn.classList.add('active-mode');
    if (mode === 'rotate' && rotateBtn) rotateBtn.classList.add('active-mode');
    
    console.log('[updateMode] Mode updated to:', mode);
}


function getCanvasElement(): HTMLElement | null {
  const mycan: HTMLElement | null = document.getElementById('roomCanvas');
  /*let canvas: SVGElement | null = null;
  
  if(mycan){
      canvas = mycan as SVGElement;
  }*/

  return mycan;
}

// Update room dimensions and redraw grid
function updateRoom() {

    const rWElement = document.getElementById('roomDiamentionW') as HTMLInputElement;
    const rHElement = document.getElementById('roomDiamentionH') as HTMLInputElement;

    let roomWidth: number = 3.0;// number = parseFloat(rWElement.value) || 3.0;
    let roomHeight: number = 2.4; // number = parseFloat(rHElement.value) || 2.4;

    if(rWElement && rHElement) {
        roomWidth = parseFloat(rWElement.value);
        roomHeight = parseFloat(rHElement.value);
    }

    let gridWidth = (roomWidth * 10);
    var gridheight = (roomHeight * 10);

    const canvas = getCanvasElement();
    if(!canvas) {
        console.error('Canvas element not found!');
        return;
    }

    canvas.setAttribute('width', (gridWidth  * gridSize).toString());
    canvas.setAttribute('height', (gridheight * gridSize).toString());
    canvas.innerHTML = ''; // Clear canvas
  
    // Draw grid
    for (let x = 0; x <= gridWidth * gridSize; x += gridSize) {
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', x.toString());
        line.setAttribute('y1', '0');
        line.setAttribute('x2', x.toString());
        line.setAttribute('y2', (gridheight * gridSize).toString());
        line.setAttribute('class', 'grid-line');
        canvas.appendChild(line);
    }
    for (let y = 0; y <= gridheight * gridSize; y += gridSize) {
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', '0');
        line.setAttribute('y1', y.toString());
        line.setAttribute('x2', (gridWidth * gridSize).toString());
        line.setAttribute('y2', y.toString());
        line.setAttribute('class', 'grid-line');
        canvas.appendChild(line);
    }
}


// Select object to place
function selectObject(objType: string) {
  
    selectedObjectType = objType;

    
    const canvas = getCanvasElement();
    if(!canvas) {
        console.error('Canvas element not found!');
        return;
    }

    // Ensure we only add the event listener once
    canvas.addEventListener('click', placeObject, { once: true });
}

function placeObject(event: PointerEvent) {
    // Place the selected object on the canvas at the clicked position
    

    if (!selectedObjectType) {

      console.error('place object failed. No selected object type!');
      return;
    }

    const canvas = getCanvasElement();
    if(!canvas) {
        console.error('Canvas element not found!');
        return;
    }

    //TODO - get the position from the event
    const rect = canvas.getBoundingClientRect();
    const x: number = Math.round((event.clientX - rect.left) / gridSize) * gridSize;
    const y = Math.round((event.clientY - rect.top) / gridSize) * gridSize;


    // SVG object definitions with dimensions (bath uses product SVG if available)
    const bathSvg = selectedProductSvgContent
      ? selectedProductSvgContent
      : '<svg width="100" height="300" viewBox="0 0 100 300"><rect x="0" y="0" width="100" height="300" fill="gray"/></svg>';

    const objectDefs: Record<string, { svg: string; width: number; height: number }> = {
        door: { svg: '<svg width="180" height="20" viewBox="0 0 180 20"><rect x="0" y="0" width="180" height="20" fill="brown"/></svg>', width: 180, height: 20 },
        window: { svg: '<svg width="200" height="20" viewBox="0 0 200 20"><rect x="0" y="0" width="200" height="20" fill="lightblue"/></svg>', width: 200, height: 20 },
        bath: { svg: bathSvg, width: 100, height: 300 },
        sink: { svg: '<svg width="80" height="80" viewBox="0 0 80 80"><circle cx="40" cy="40" r="40" fill="silver"/></svg>', width: 80, height: 80 }
    };

    const objDef = objectDefs[selectedObjectType];
    if (!objDef) {
        console.error('Unknown object type:', selectedObjectType);
        return;
    }

    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    g.setAttribute('transform', `translate(${x}, ${y})`);

    g.id = `obj-${Date.now()}`; // Unique ID based on timestamp
    g.setAttribute('data-x', x.toString());
    g.setAttribute('data-y', y.toString());
    g.setAttribute('data-width', objDef.width.toString());
    g.setAttribute('data-height', objDef.height.toString());
    g.setAttribute('data-rotation', '0'); // Initialize rotation at 0 degrees
    g.setAttribute('data-type', selectedObjectType);
    g.setAttribute('class', 'object selected');

    // For bath objects, persist the SVG asset path so it can be restored on reload
    if (selectedObjectType === 'bath' && selectedProductSvgSrc) {
      g.setAttribute('data-svg-src', selectedProductSvgSrc);
    }

    // Insert the SVG markup
    g.innerHTML = objDef.svg;

    //Add move handle
    const handle = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    handle.setAttribute('width', `${handleSize}`);  // Increase size for easier selection
    handle.setAttribute('height', `${handleSize}`);
    handle.setAttribute('fill', 'blue');
    handle.setAttribute('class', 'move-handle');
    handle.setAttribute('x', '4'); // Adjust position slightly to make it more accessible
    handle.setAttribute('y', '4');

    g.appendChild(handle);


    canvas.appendChild(g);

    // **Initialize dragging for this object**
    initializeObjectDragging(g);

    // **Highlight selection**
    g.addEventListener('click', (e) => {
        e.stopPropagation();
        document.querySelectorAll('.object').forEach(obj => obj.classList.remove('selected'));
        g.classList.add('selected');

        // Update panel values including width and height
        updatePropertyPanel(g);
    });

    // **Enable rotation on click if in rotate mode**
    g.addEventListener('click', () => {
      console.log('Object clicked for rotation check. Current mode:', mode);

      if (mode === "rotate") {
          let rotAttribute = g.getAttribute('data-rotation');
          let currentRotation = 0; 
          if(rotAttribute) {
            currentRotation = parseFloat(rotAttribute);
          }
          let newRotation = (currentRotation + 90) % 360; // Rotates by 90-degree increments

          // Get bounding box to calculate center
          let bbox = g.getBBox();
          let centerX = bbox.x + bbox.width / 2;
          let centerY = bbox.y + bbox.height / 2;

          // Apply rotation transform around the object's center
          g.setAttribute('data-rotation', newRotation.toString());
          g.setAttribute('transform', `translate(${g.getAttribute('data-x')}, ${g.getAttribute('data-y')}) rotate(${newRotation}, ${centerX}, ${centerY})`);
          
          // Update property panel
          updatePropertyPanel(g);
      }
    });

    selectedObjectType = ''; // Clear selection
    canvas.removeEventListener('click', placeObject);
}

// Helper function to update property panel with object values
function updatePropertyPanel(object: SVGGElement) {
    const elePropX = document.getElementById('propX') as HTMLInputElement;
    const elePropY = document.getElementById('propY') as HTMLInputElement;
    const elePropWidth = document.getElementById('propWidth') as HTMLInputElement;
    const elePropHeight = document.getElementById('propHeight') as HTMLInputElement;
    const elePropRotation = document.getElementById('propRotation') as HTMLInputElement;

    if (elePropX) elePropX.value = object.getAttribute('data-x') || '0';
    if (elePropY) elePropY.value = object.getAttribute('data-y') || '0';
    if (elePropWidth) elePropWidth.value = object.getAttribute('data-width') || '0';
    if (elePropHeight) elePropHeight.value = object.getAttribute('data-height') || '0';
    if (elePropRotation) elePropRotation.value = object.getAttribute('data-rotation') || '0';
}

// Set the interaction mode (move, resize, rotate)
function setMode(newMode: string) {
  mode = newMode;
  console.log(`[setMode] Mode changed to: ${mode}`);
  
  // Update body class for cursor styling
  document.body.classList.remove('move-mode', 'resize-mode', 'rotate-mode');
  document.body.classList.add(`${mode}-mode`);
  
  // Update mode display (if visible)
  updateMode();
}

// Remove the currently selected object
function removeSelectedObject() {
  const selectedObj = document.querySelector('.object.selected') as SVGGElement;
  if (selectedObj) {
    selectedObj.remove();
    console.log('[removeSelectedObject] Object removed');
    
    // Clear property panel
    const elePropX = document.getElementById('propX') as HTMLInputElement;
    const elePropY = document.getElementById('propY') as HTMLInputElement;
    const elePropWidth = document.getElementById('propWidth') as HTMLInputElement;
    const elePropHeight = document.getElementById('propHeight') as HTMLInputElement;
    const elePropRotation = document.getElementById('propRotation') as HTMLInputElement;
    
    if (elePropX) elePropX.value = '0';
    if (elePropY) elePropY.value = '0';
    if (elePropWidth) elePropWidth.value = '0';
    if (elePropHeight) elePropHeight.value = '0';
    if (elePropRotation) elePropRotation.value = '0';
  } else {
    console.warn('[removeSelectedObject] No object selected');
  }
}

// Apply property changes from the property panel to the selected object
function applyPropertyChanges() {
  const selectedObj = document.querySelector('.object.selected') as SVGGElement;
  if (!selectedObj) {
    console.warn('[applyPropertyChanges] No object selected');
    return;
  }
  
  const elePropX = document.getElementById('propX') as HTMLInputElement;
  const elePropY = document.getElementById('propY') as HTMLInputElement;
  const elePropWidth = document.getElementById('propWidth') as HTMLInputElement;
  const elePropHeight = document.getElementById('propHeight') as HTMLInputElement;
  const elePropRotation = document.getElementById('propRotation') as HTMLInputElement;
  
  const newX = parseFloat(elePropX.value) || 0;
  const newY = parseFloat(elePropY.value) || 0;
  const newWidth = parseFloat(elePropWidth.value) || 20;
  const newHeight = parseFloat(elePropHeight.value) || 20;
  const newRotation = parseFloat(elePropRotation.value) || 0;
  
  // Snap to grid
  const snappedX = Math.round(newX / gridSize) * gridSize;
  const snappedY = Math.round(newY / gridSize) * gridSize;
  const snappedWidth = Math.round(newWidth / gridSize) * gridSize;
  const snappedHeight = Math.round(newHeight / gridSize) * gridSize;
  
  // Update data attributes
  selectedObj.setAttribute('data-x', snappedX.toString());
  selectedObj.setAttribute('data-y', snappedY.toString());
  selectedObj.setAttribute('data-width', snappedWidth.toString());
  selectedObj.setAttribute('data-height', snappedHeight.toString());
  selectedObj.setAttribute('data-rotation', newRotation.toString());
  
  // Update the SVG element's dimensions
  const svgElement = selectedObj.querySelector('svg');
  if (svgElement) {
    svgElement.setAttribute('width', snappedWidth.toString());
    svgElement.setAttribute('height', snappedHeight.toString());
    
    // Update viewBox to maintain aspect ratio
    const rect = svgElement.querySelector('rect');
    const circle = svgElement.querySelector('circle');
    if (rect) {
      rect.setAttribute('width', snappedWidth.toString());
      rect.setAttribute('height', snappedHeight.toString());
    }
    if (circle) {
      const radius = Math.min(snappedWidth, snappedHeight) / 2;
      circle.setAttribute('cx', (snappedWidth / 2).toString());
      circle.setAttribute('cy', (snappedHeight / 2).toString());
      circle.setAttribute('r', radius.toString());
    }
  }
  
  // Apply transform
  const bbox = selectedObj.getBBox();
  const centerX = bbox.width / 2;
  const centerY = bbox.height / 2;
  selectedObj.setAttribute('transform', `translate(${snappedX}, ${snappedY}) rotate(${newRotation}, ${centerX}, ${centerY})`);
  
  // Update input fields with snapped values
  elePropX.value = snappedX.toString();
  elePropY.value = snappedY.toString();
  elePropWidth.value = snappedWidth.toString();
  elePropHeight.value = snappedHeight.toString();
  
  console.log('[applyPropertyChanges] Properties applied:', { x: snappedX, y: snappedY, width: snappedWidth, height: snappedHeight, rotation: newRotation });
}



function initializeObjectDragging(element: SVGGElement) {
  // Make the ENTIRE object draggable in move mode, not just the handle
  element.addEventListener('mousedown', (e: MouseEvent) => {
    // Only allow dragging in move mode
    if (mode !== 'move') return;
    
    e.stopPropagation();
    e.preventDefault();
    
    // Start dragging
    dragState.isDragging = true;
    dragState.startX = e.clientX;
    dragState.startY = e.clientY;
    dragState.element = element;
    
    // Store initial position
    dragState.initialX = parseFloat(element.getAttribute('data-x') || '0');
    dragState.initialY = parseFloat(element.getAttribute('data-y') || '0');
    
    // Select this object
    document.querySelectorAll('.object').forEach(obj => obj.classList.remove('selected'));
    element.classList.add('selected');
    updatePropertyPanel(element);
    
    console.log('[initializeObjectDragging] Drag started at', { x: dragState.startX, y: dragState.startY });
  });
}

// Global mouse move handler for smooth dragging
document.addEventListener('mousemove', (e: MouseEvent) => {
  if (!dragState.isDragging || !dragState.element) return;
  
  // Calculate total displacement from start position
  const dx = e.clientX - dragState.startX;
  const dy = e.clientY - dragState.startY;
  
  // Calculate new position (smooth, no snapping during drag)
  let newX = dragState.initialX + dx;
  let newY = dragState.initialY + dy;
  
  // Update position smoothly WITHOUT grid snapping (for smooth feel)
  dragState.element.setAttribute('data-x', newX.toString());
  dragState.element.setAttribute('data-y', newY.toString());
  
  const rotation = dragState.element.getAttribute('data-rotation') || '0';
  const bbox = dragState.element.getBBox();
  const centerX = bbox.width / 2;
  const centerY = bbox.height / 2;
  dragState.element.setAttribute('transform', `translate(${newX}, ${newY}) rotate(${rotation}, ${centerX}, ${centerY})`);
  
  // Update property panel in real-time
  updatePropertyPanel(dragState.element);
});

// Global mouse up handler - snap to grid on release
document.addEventListener('mouseup', () => {
  if (dragState.isDragging && dragState.element) {
    console.log('[initializeObjectDragging] Drag ended - snapping to grid');
    
    // Snap to grid on release
    const currentX = parseFloat(dragState.element.getAttribute('data-x') || '0');
    const currentY = parseFloat(dragState.element.getAttribute('data-y') || '0');
    
    const snappedX = Math.round(currentX / gridSize) * gridSize;
    const snappedY = Math.round(currentY / gridSize) * gridSize;
    
    dragState.element.setAttribute('data-x', snappedX.toString());
    dragState.element.setAttribute('data-y', snappedY.toString());
    
    const rotation = dragState.element.getAttribute('data-rotation') || '0';
    const bbox = dragState.element.getBBox();
    const centerX = bbox.width / 2;
    const centerY = bbox.height / 2;
    dragState.element.setAttribute('transform', `translate(${snappedX}, ${snappedY}) rotate(${rotation}, ${centerX}, ${centerY})`);
    
    // Update property panel with snapped values
    updatePropertyPanel(dragState.element);
    
    dragState.isDragging = false;
    dragState.element = null;
  }
});

// Save drawing data to orderData
async function saveDrawingData() {
  const canvas = getCanvasElement();
  if (!canvas) {
    console.error('[saveDrawingData] Canvas element not found');
    return;
  }
  
  const objects = canvas.querySelectorAll('.object');
  const objects2d: Object2d[] = [];
  
  objects.forEach((obj) => {
    const element = obj as SVGGElement;
    const object2d: Object2d = {
      id: element.id,
      type: element.getAttribute('data-type') || '',
      x: parseFloat(element.getAttribute('data-x') || '0'),
      y: parseFloat(element.getAttribute('data-y') || '0'),
      width: parseFloat(element.getAttribute('data-width') || '0'),
      height: parseFloat(element.getAttribute('data-height') || '0'),
      rotation: parseFloat(element.getAttribute('data-rotation') || '0')
    };
    objects2d.push(object2d);
  });
  
  // Save to orderData
  if (!orderData.step_data.steps.step5.data.drawingData) {
    orderData.step_data.steps.step5.data.drawingData = { isLocked: false, objects2d: [] };
  }
  orderData.step_data.steps.step5.data.drawingData.objects2d = objects2d;

  const rWElement = document.getElementById('roomDiamentionW') as HTMLInputElement;
  const rHElement = document.getElementById('roomDiamentionH') as HTMLInputElement;
  if(rWElement) {
    const rwValue: number = parseFloat(rWElement.value as string);
    orderData.step_data.steps.step2.data.propertyData.roomDiamentionW = rwValue;
  }
  if(rHElement) {
    const rHValue: number = parseFloat(rHElement.value as string);
    orderData.step_data.steps.step2.data.propertyData.roomDiamentionH = rHValue;
  }
  
  console.log('[saveDrawingData] Saved drawing data:', objects2d);
  await saveOrder(orderData);
}

// Load drawing data from orderData
async function loadDrawingData(roomDiamentionWidth: number, roomDiamentionHeight: number) {
  const canvas = getCanvasElement();
  if (!canvas) {
    console.error('[loadDrawingData] Canvas element not found');
    return;
  }
  
  const drawingData = orderData.step_data.steps.step5.data.drawingData;
  if (!drawingData || !drawingData.objects2d || drawingData.objects2d.length === 0) {
    console.log('[loadDrawingData] No drawing data to load');
    return;
  }
  
  console.log('[loadDrawingData] Loading drawing data:', drawingData.objects2d);

  const rWElement = document.getElementById('roomDiamentionW') as HTMLInputElement;
  const rHElement = document.getElementById('roomDiamentionH') as HTMLInputElement;
  rWElement.value = roomDiamentionWidth.toString();
  rHElement.value = roomDiamentionHeight.toString();

  // Resolve the product range SVG path from saved order data (used for bath objects)
  let bathSvgForLoad: string = '<svg width="100" height="300" viewBox="0 0 100 300"><rect x="0" y="0" width="100" height="300" fill="gray"/></svg>';
  const savedProductRange = orderData.step_data.steps.step3.data.productRange;
  if (savedProductRange && savedProductRange !== 0 && cachedClientOptions) {
    const rangeOption = cachedClientOptions.find(
      item => item.field_key === 'product_range' && item.field_value === savedProductRange.toString()
    );
    if (rangeOption?.field_title) {
      const svgFilename = rangeOption.field_title.toLowerCase().replace(/\s+/g, '_') + '.svg';
      const svgPath = `/assets/products/${svgFilename}`;
      try {
        const response = await fetch(svgPath);
        if (response.ok) {
          bathSvgForLoad = await response.text();
          console.log(`[loadDrawingData] Loaded product SVG for bath restore: ${svgPath}`);
        } else {
          console.warn(`[loadDrawingData] Bath SVG not found at ${svgPath}, using default`);
        }
      } catch (err) {
        console.warn(`[loadDrawingData] Failed to fetch bath SVG: ${err}`);
      }
    }
  }

  // SVG object definitions with dimensions
  const objectDefs: Record<string, { svg: string }> = {
    door: { svg: '<svg width="180" height="20" viewBox="0 0 180 20"><rect x="0" y="0" width="180" height="20" fill="brown"/></svg>' },
    window: { svg: '<svg width="200" height="20" viewBox="0 0 200 20"><rect x="0" y="0" width="200" height="20" fill="lightblue"/></svg>' },
    bath: { svg: bathSvgForLoad },
    sink: { svg: '<svg width="80" height="80" viewBox="0 0 80 80"><circle cx="40" cy="40" r="40" fill="silver"/></svg>' }
  };
  
  drawingData.objects2d.forEach((obj) => {
    const objDef = objectDefs[obj.type];
    if (!objDef) {
      console.warn('[loadDrawingData] Unknown object type:', obj.type);
      return;
    }
    
    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    g.setAttribute('transform', `translate(${obj.x}, ${obj.y}) rotate(${obj.rotation})`);
    g.id = obj.id;
    g.setAttribute('data-x', obj.x.toString());
    g.setAttribute('data-y', obj.y.toString());
    g.setAttribute('data-width', obj.width.toString());
    g.setAttribute('data-height', obj.height.toString());
    g.setAttribute('data-rotation', obj.rotation.toString());
    g.setAttribute('data-type', obj.type);
    g.setAttribute('class', 'object');
    
    // Insert the SVG markup with saved dimensions
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = objDef.svg;
    const svgElement = tempDiv.querySelector('svg');
    if (svgElement) {
      svgElement.setAttribute('width', obj.width.toString());
      svgElement.setAttribute('height', obj.height.toString());
      
      // Update inner shapes (only for simple rect/circle shapes; product SVGs are used as-is)
      if (obj.type !== 'bath') {
        const rect = svgElement.querySelector('rect');
        const circle = svgElement.querySelector('circle');
        if (rect) {
          rect.setAttribute('width', obj.width.toString());
          rect.setAttribute('height', obj.height.toString());
        }
        if (circle) {
          const radius = Math.min(obj.width, obj.height) / 2;
          circle.setAttribute('cx', (obj.width / 2).toString());
          circle.setAttribute('cy', (obj.height / 2).toString());
          circle.setAttribute('r', radius.toString());
        }
      }
      g.innerHTML = svgElement.outerHTML;
    } else {
      g.innerHTML = objDef.svg;
    }
    
    // Add move handle
    const handle = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    handle.setAttribute('width', `${handleSize}`);
    handle.setAttribute('height', `${handleSize}`);
    handle.setAttribute('fill', 'blue');
    handle.setAttribute('class', 'move-handle');
    handle.setAttribute('x', '4');
    handle.setAttribute('y', '4');
    g.appendChild(handle);
    
    canvas.appendChild(g);
    
    // Initialize dragging for loaded object
    initializeObjectDragging(g);
    
    // Add selection handler
    g.addEventListener('click', (e) => {
      e.stopPropagation();
      document.querySelectorAll('.object').forEach(obj => obj.classList.remove('selected'));
      g.classList.add('selected');
      updatePropertyPanel(g);
    });
    
    // Add rotation handler
    g.addEventListener('click', () => {
      if (mode === "rotate") {
        let currentRotation = parseFloat(g.getAttribute('data-rotation') || '0');
        let newRotation = (currentRotation + 90) % 360;
        
        let bbox = g.getBBox();
        let centerX = bbox.x + bbox.width / 2;
        let centerY = bbox.y + bbox.height / 2;
        
        g.setAttribute('data-rotation', newRotation.toString());
        g.setAttribute('transform', `translate(${g.getAttribute('data-x')}, ${g.getAttribute('data-y')}) rotate(${newRotation}, ${centerX}, ${centerY})`);
        
        updatePropertyPanel(g);
      }
    });
  });
  
  console.log('[loadDrawingData] Loaded', drawingData.objects2d.length, 'objects');
}

async function intializeTandCs() {
  const termsBtn = document.getElementById('termsBtn') as HTMLButtonElement;
  // Add click handler to open terms modal
  termsBtn.addEventListener('click', async () => {
    await openTandCModal();
  });


}

async function openTandCModal(){

  const res = await fetch('/assets/docs/terms.md');
  const text = await res.text();

  const modal = document.createElement('div');
  modal.className = 'fullscreen-modal signature-modal';
  modal.innerHTML = `
    <div class="fullscreen-modal-header">
      <h3>Terms and Conditions</h3>
      <button class="fullscreen-modal-close">×</button>
    </div>
    <div class="fullscreen-modal-content">
      <div id="tc_content" class="tc-content"></div> 
    </div>
  `;
  document.body.appendChild(modal);

  const html = simpleMarkdownToHtml(text);
  document.getElementById('tc_content')!.innerHTML = html;

  // Close button with confirmation
  const closeBtn = modal.querySelector('.fullscreen-modal-close');
  if (closeBtn) {
    closeBtn.addEventListener('click', async () => {
      document.body.removeChild(modal);
    });
  }
}

function simpleMarkdownToHtml(md: string): string {
  return md
    // headings
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')

    // bold & italic
    .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/gim, '<em>$1</em>')

    // unordered lists
    .replace(/^\s*-\s+(.*)/gim, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)/gim, '<ul>$1</ul>')

    // paragraphs
    .replace(/\n$/gim, '<br />');
}



/**
 * Initialize signature preview area - show existing signature or canvas for new signature
 */
async function initializeSignaturePreview() {
  console.log('[initializeSignaturePreview] Setting up signature preview area');
  
  const signoffData = orderData.step_data.steps.step6.data;
  const previewArea = document.getElementById('signature-preview-area');
  const deleteBtn = document.getElementById('delete-signature-btn');
  
  if (!previewArea) {
    console.error('[initializeSignaturePreview] Preview area not found');
    return;
  }
  
  // Check if signature exists
  if (signoffData.clientSignature) {
    console.log('[initializeSignaturePreview] Loading existing signature:', signoffData.clientSignature);
    
    try {
      // Load signature image from database (step 6)
      const images = await getImagesByOrderAndStep(orderId, 6);
      
      if (images && images.length > 0) {
        // Display the first (and should be only) signature image
        const imageData = images[0];
        const blobData = imageData.blob.slice(0);
        const blob = new Blob([blobData], { type: 'image/jpeg' });
        const blobUrl = URL.createObjectURL(blob);
        
        // Show signature image
        previewArea.innerHTML = `<img src="${blobUrl}" alt="Customer Signature" style="width: 100%; height: 100%; object-fit: contain;">`;
        previewArea.style.background = '#fff';
        
        // Show delete button
        if (deleteBtn) {
          deleteBtn.style.display = 'block';
        }
        
        console.log('[initializeSignaturePreview] Signature image displayed');
      } else {
        console.warn('[initializeSignaturePreview] Signature ID exists but no image found');
        // Reset to canvas view
        previewArea.innerHTML = '<span style="color: #999;">Click to add signature</span>';
        if (deleteBtn) deleteBtn.style.display = 'none';
      }
    } catch (error) {
      console.error('[initializeSignaturePreview] Error loading signature:', error);
      previewArea.innerHTML = '<span style="color: #999;">Click to add signature</span>';
      if (deleteBtn) deleteBtn.style.display = 'none';
    }
  } else {
    // No signature - show default state
    previewArea.innerHTML = '<span style="color: #999;">Click to add signature</span>';
    if (deleteBtn) deleteBtn.style.display = 'none';
  }
  
  // Add click handler to open signature modal
  previewArea.addEventListener('click', () => {
    openSignatureModal();
  });
  
  // Add delete button handler
  if (deleteBtn) {
    deleteBtn.addEventListener('click', async (e) => {
      e.stopPropagation(); // Prevent opening modal
      
      const confirmed = await showConfirmationModal({
        title: 'Delete Signature?',
        message: 'Are you sure you want to delete the customer signature?',
        modalType: 'warning',
        buttons: [
          { label: 'Delete', value: 'delete', style: 'danger' },
          { label: 'Cancel', value: 'cancel', style: 'secondary', isDefault: true }
        ]
      });
      
      if (confirmed === 'delete') {
        try {
          // Get images for step 6
          const images = await getImagesByOrderAndStep(orderId, 6);
          
          if (images && images.length > 0) {
            // Delete the signature image
            await deleteImage(images[0].id);
            console.log('[initializeSignaturePreview] Signature image deleted');
          }
          
          // Clear clientSignature field
          orderData.step_data.steps.step6.data.clientSignature = undefined;
          await saveOrder(orderData);
          
          // Refresh preview
          await initializeSignaturePreview();
          
        } catch (error) {
          console.error('[initializeSignaturePreview] Error deleting signature:', error);
          alert('Failed to delete signature. Please try again.');
        }
      }
    });
  }
}

/**
 * Open fullscreen signature modal for capturing customer signature
 */
function openSignatureModal() {
  console.log('[openSignatureModal] Opening signature capture modal');
  
  const modal = document.createElement('div');
  modal.className = 'fullscreen-modal signature-modal';
  modal.innerHTML = `
    <div class="fullscreen-modal-header">
      <h3>Customer Signature</h3>
      <button class="fullscreen-modal-close">×</button>
    </div>
    <div class="fullscreen-modal-content">
      <canvas id="signature-fullscreen-canvas" width="1200" height="400" style="border: 2px solid #ccc; background: #fff; cursor: crosshair;"></canvas>
      <div class="signature-modal-controls" style="margin-top: 20px; display: flex; gap: 15px; justify-content: center;">
        <button id="clear-signature-modal" class="btn btn-secondary">Clear</button>
        <button id="save-signature-modal" class="btn btn-primary">Save Signature</button>
      </div>
    </div>
  `;
  
  document.body.appendChild(modal);
  
  // Get canvas and set up drawing
  const canvas = document.getElementById('signature-fullscreen-canvas') as HTMLCanvasElement;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    console.error('[openSignatureModal] Failed to get canvas context');
    return;
  }
  
  // Drawing state
  let isDrawing = false;
  let lastX = 0;
  let lastY = 0;
  let hasContent = false;
  
  // Configure drawing style
  ctx.strokeStyle = '#000';
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  
  // Mouse events
  canvas.addEventListener('mousedown', (e) => {
    isDrawing = true;
    const rect = canvas.getBoundingClientRect();
    lastX = e.clientX - rect.left;
    lastY = e.clientY - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
  });
  
  canvas.addEventListener('mousemove', (e) => {
    if (!isDrawing) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
    
    lastX = x;
    lastY = y;
    hasContent = true;
  });
  
  canvas.addEventListener('mouseup', () => {
    isDrawing = false;
  });
  
  canvas.addEventListener('mouseleave', () => {
    isDrawing = false;
  });
  
  // Touch events for mobile
  canvas.addEventListener('touchstart', (e) => {
    e.preventDefault();
    isDrawing = true;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    lastX = touch.clientX - rect.left;
    lastY = touch.clientY - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
  });
  
  canvas.addEventListener('touchmove', (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
    
    lastX = x;
    lastY = y;
    hasContent = true;
  });
  
  canvas.addEventListener('touchend', (e) => {
    e.preventDefault();
    isDrawing = false;
  });
  
  // Clear button
  const clearBtn = document.getElementById('clear-signature-modal');
  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      hasContent = false;
      console.log('[openSignatureModal] Canvas cleared');
    });
  }
  
  // Save button
  const saveBtn = document.getElementById('save-signature-modal');
  if (saveBtn) {
    saveBtn.addEventListener('click', async () => {
      if (!hasContent) {
        alert('Please add a signature before saving.');
        return;
      }
      
      await saveSignatureFromCanvas(canvas);
      document.body.removeChild(modal);

      let signedValue = document.getElementById('lastSignedDate') as HTMLInputElement;
      if(signedValue){
        signedValue.value = new Date().toISOString();
      }
      updateSignedDate();

    });
  }
  
  // Close button with confirmation
  const closeBtn = modal.querySelector('.fullscreen-modal-close');
  if (closeBtn) {
    closeBtn.addEventListener('click', async () => {
      if (hasContent) {
        const result = await showConfirmationModal({
          title: 'Save Signature?',
          message: 'Would you like to save the signature before closing?',
          modalType: 'info',
          buttons: [
            { label: 'Save Signature', value: 'save', style: 'primary' },
            { label: 'Discard Changes', value: 'discard', style: 'danger' },
            { label: 'Keep Editing', value: 'cancel', style: 'secondary', isDefault: true }
          ]
        });
        
        if (result === 'save') {
          await saveSignatureFromCanvas(canvas);
          document.body.removeChild(modal);
        } else if (result === 'discard') {
          document.body.removeChild(modal);
        }
        // If 'cancel', do nothing - stay in modal
      } else {
        document.body.removeChild(modal);
      }
    });
  }
  
  // Close on background click
  modal.addEventListener('click', async (e) => {
    if (e.target === modal) {
      if (closeBtn) {
        (closeBtn as HTMLElement).click();
      }
    }
  });
}

function updateSignedDate(){
  let signedValue = document.getElementById('lastSignedDate') as HTMLInputElement;
  let signedDateLbl = document.getElementById("dateSigned") as HTMLLabelElement;

  if (signedDateLbl && signedValue?.value) {
    const date = new Date(signedValue.value);

    signedDateLbl.innerText = date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  }
}

/**
 * Save signature from canvas to database
 */
async function saveSignatureFromCanvas(canvas: HTMLCanvasElement) {
  console.log('[saveSignatureFromCanvas] Saving signature to database');
  
  try {
    // Convert canvas to blob with compression
    const blob = await new Promise<Blob>((resolve, reject) => {
      // Create a temporary canvas for resizing to 600x200
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = 600;
      tempCanvas.height = 200;
      const tempCtx = tempCanvas.getContext('2d');
      
      if (!tempCtx) {
        reject(new Error('Failed to get temp canvas context'));
        return;
      }
      
      // Draw scaled signature to temp canvas
      tempCtx.fillStyle = '#fff';
      tempCtx.fillRect(0, 0, 600, 200);
      tempCtx.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, 600, 200);
      
      // Convert to compressed JPEG
      tempCanvas.toBlob(
        (b) => {
          if (b) {
            resolve(b);
          } else {
            reject(new Error('Failed to create blob'));
          }
        },
        'image/jpeg',
        0.5 // 50% quality - sufficient for signatures
      );
    });
    
    // Create file from blob
    const timestamp = new Date().getTime();
    const file = new File([blob], `signature_${timestamp}.jpg`, {
      type: 'image/jpeg',
      lastModified: timestamp
    });
    
    // Delete any existing signature first
    const existingImages = await getImagesByOrderAndStep(orderId, 6);
    if (existingImages && existingImages.length > 0) {
      for (const img of existingImages) {
        await deleteImage(img.id);
      }
      console.log('[saveSignatureFromCanvas] Deleted existing signature');
    }
    
    // Save new signature to database (step 6)
    await saveImage(orderId, 6, file, 'Client Signature');
    console.log('[saveSignatureFromCanvas] Signature saved to database');
    
    // Get the new image ID and update orderData
    const newImages = await getImagesByOrderAndStep(orderId, 6);
    if (newImages && newImages.length > 0) {
      orderData.step_data.steps.step6.data.clientSignature = newImages[0].id.toString();
      await saveOrder(orderData);
      console.log('[saveSignatureFromCanvas] Updated clientSignature field:', orderData.step_data.steps.step6.data.clientSignature);
    }
    
    // Refresh signature preview
    await initializeSignaturePreview();
    
  } catch (error) {
    console.error('[saveSignatureFromCanvas] Error saving signature:', error);
    alert('Failed to save signature. Please try again.');
  }
}

async function prevStep() { //steps: { title: string; component: HTMLElement }[]
  if (currentStep > 0) {
    // Capture prices from current step BEFORE navigating away
    capturePricesFromCurrentStep();
    
    // When leaving the Signoff step (step 5), recalculate and save the total
    if (currentStep === 5) {
      updateOrderSummaryAndTotal();
      console.log('[prevStep] Saving order with updated totalContractPrice when leaving Signoff:', orderData.totalContractPrice);
      saveOrder(orderData);
    }
    
    // Clean up signature pad when leaving step 5 (Signoff)
    if (currentStep === 5 && signaturePadInstance) {
      signaturePadInstance = null;
    }
    
    saveCurrentStep();
    currentStep--;
    
    // Recreate steps array with latest data from orderData
    const updatedSteps = await createStepsArray();
    renderStep(updatedSteps);
    updateStepProgress();
  }
}

async function nextStep(steps: { title: string; component: HTMLElement }[]) {


  // If on the final step (step 5), call closeWizard instead
  if (currentStep === 5) {
    await closeWizard();
    return;
  }
  
  const form = document.querySelector('.step-card') as HTMLFormElement;
  
  // Validate form if it has inputs
  const inputs = form?.querySelectorAll('input[required], select[required]');
  if (inputs && inputs.length > 0) {
    let isValid = true;
    inputs.forEach(input => {
      const element = input as HTMLInputElement | HTMLSelectElement;
      if (!element.value || element.value.trim() === '') {
        isValid = false;
        element.style.borderColor = 'red';
      } else {
        element.style.borderColor = '';
      }
    });
    
    if (!isValid) {
      alert('Please fill in all required fields');
      return;
    }
  }


  //here perform a check on the product selection
  if (currentStep === 2) {


    if(incompatibilityLookups && packsByRange){
      const ctx = buildCurrentPackSelectionContext();
      const result = validateCurrentConfiguration(ctx, {
        packsByRange,
        incompat: incompatibilityLookups
      });

      if (!result.isValid) {
        alert('You have selected invalid options for the product select. Please corrent before proceeding.');
        renderValidationErrors(result.errors);
        return;
      }
    }

    //const productBathScreenOptionSelect = document.getElementById("productScreenOption")! as HTMLSelectElement;
    //const selecedBathScreenId = parseInt(productBathScreenOptionSelect.value, 10);

    //check the requirements from pack is valid.



  }


  // Capture prices from current step BEFORE navigating away
  capturePricesFromCurrentStep();
  
  saveCurrentStep();
  currentStep++;
  
  if (currentStep < steps.length) {
    // Recreate steps array with latest data from orderData
    const updatedSteps = await createStepsArray();
    renderStep(updatedSteps);
    updateStepProgress();
    
    // When entering the Signoff step (step 5), recalculate order summary and total
    // This needs to happen AFTER renderStep() so all forms with prices are in the DOM
    if (currentStep === 5) {
      setTimeout(() => {
        // Rebuild stored prices from saved order data to ensure we have the latest
        console.log('[nextStep] Rebuilding price data before generating summary...');
        rebuildStoredPricesFromOrderData();
        
        updateOrderSummaryAndTotal();
        
        // Update the textarea and total price fields with the latest values
        const orderSummaryTextarea = document.getElementById('orderSummary') as HTMLTextAreaElement;
        const totalContractPriceInput = document.getElementById('totalContractPrice') as HTMLInputElement;
        
        if (orderSummaryTextarea) {
          orderSummaryTextarea.value = orderData.step_data.steps.step6.data.orderSummary;
        }
        if (totalContractPriceInput) {
          totalContractPriceInput.value = `£${orderData.totalContractPrice.toFixed(2)}`;
        }
        
        // Save the updated order with the new totalContractPrice
        console.log('[nextStep] Saving order with updated totalContractPrice:', orderData.totalContractPrice);
        saveOrder(orderData);
      }, 150);
    }
  } else {
    // Complete the order
    orderData.status = 'completed';
    saveOrder(orderData);
    //syncOrders().catch(console.error);
    navigate('/dashboard');
  }
}




function validatePackSelections(
  ctx: PackSelectionContext,
  incompat: {
    optionVariant: Set<string>;
    optionOption: Set<string>;
  }
): {
  valid: boolean;
  errors: string[];
} {

  const errors: string[] = [];

  // 1. Required pack check
  let requiredPacks: PackCategory[] = [];// = deps.packsByRange[0].categories
  //console.log(`validatePackSelections for range: ${ctx.rangeId}`, packsByRange);
  const rngIdx = packsByRange.findIndex(x => x.rangeId === ctx.rangeId);
  if(rngIdx > -1) {
    const matchingPack: ProductPackCache = packsByRange[rngIdx];
    //console.log(`matchingPack cache:`, matchingPack);
    if(matchingPack && matchingPack.categories) {
      requiredPacks = matchingPack.categories.slice();
    }
  }
  //const requiredPacks = packsByRange?.get(ctx.rangeId) || [];

  for (const pcat of requiredPacks) {
    if (!ctx.selectedOptionsByPack[pcat.packKey]) {
      errors.push(`Missing required selection: ${pcat.packKey}`);
    }
  }

  // 2. Option ↔ Variant compatibility
  for (const optionId of Object.values(ctx.selectedOptionsByPack)) {
    if (
      incompat.optionVariant.has(`${optionId}:${ctx.variantId}`)
    ) {
      errors.push(
        `Option ${optionId} incompatible with selected variant`
      );
    }
  }

  // 3. Option ↔ Option compatibility
  const selectedOptions = Object.values(ctx.selectedOptionsByPack);

  for (let i = 0; i < selectedOptions.length; i++) {
    for (let j = i + 1; j < selectedOptions.length; j++) {
      const a = selectedOptions[i];
      const b = selectedOptions[j];

      if (
        incompat.optionOption.has(`${a}:${b}`)
      ) {
        errors.push(
          `Options ${a} and ${b} cannot be combined`
        );
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

function bindPackValidation(): void {

  //console.log("bindPackValidation");
  

  

  const selects: NodeListOf<HTMLSelectElement> = document.querySelectorAll<HTMLSelectElement>(
    'select[data-pack-key]'
  );

  selects.forEach(select => {
    select.removeEventListener('change', packSelectionOptionChange.bind(select, selects));
    select.addEventListener('change', packSelectionOptionChange.bind(select, selects));
  });
}

function packSelectionOptionChange(
  selects: NodeListOf<HTMLSelectElement>
) {

    const rangeSelect = document.getElementById("productRange") as HTMLSelectElement;
    const variantSelect = document.getElementById("productVariant") as HTMLSelectElement;

    const currentRangeId = rangeSelect.value;
    const currentVariantId = variantSelect.value;

    const ctx: PackSelectionContext = {
      rangeId: parseInt(currentRangeId, 10),
      variantId: currentVariantId,
      selectedOptionsByPack: {}
    };

    selects.forEach(s => {
      const packKey = s.dataset.packKey!;
      if (s.value) {
        ctx.selectedOptionsByPack[packKey] = s.value;
      }
    });

    if(incompatibilityLookups) {
      const result = validatePackSelections(
        ctx,
        incompatibilityLookups
      );

      //Write out all the current selected pack selections
      const psContainer = document.getElementById("pack-debug")!;
      let summaryHtml: string = "";
      for(const pso in ctx.selectedOptionsByPack) {
        summaryHtml += pso.toString() + " = " + ctx.selectedOptionsByPack[pso] + "<br /> ";
      }
      psContainer.innerHTML = summaryHtml;

      renderPackValidation(result);
    }
    else {
      const container = document.getElementById("pack-validation")!;
      container.innerText = "No validations assigned.";
    }
}

function clearPackSummary() {
  const container = document.getElementById("pack-summary")!;
  container.innerHTML = "";
}

function clearPackValidation() {
  const container = document.getElementById("pack-validation")!;
  container.innerHTML = "";
}

function renderPackValidation(result: {
  valid: boolean;
  errors: string[];
}): void {

  clearPackValidation();
  const container = document.getElementById("pack-validation")!;

  if (result.valid) {
    container.textContent = "✓ Configuration valid";
    container.className = "valid";
    return;
  }

  result.errors.forEach(err => {
    const div = document.createElement("div");
    div.textContent = err;
    div.className = "error";
    container.appendChild(div);
  });
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

export interface ValidationError {
  type: "missing_pack" | "option_variant" | "option_option";
  message: string;
  packKey?: string;
  optionId?: string;
}

export function validateCurrentConfiguration(
  ctx: PackSelectionContext,
  deps: {
    packsByRange: ProductPackCache[];
    incompat: {
      optionVariant: Set<string>;
      optionOption: Set<string>;
    };
  }
): ValidationResult {

  const errors: ValidationError[] = [];

  // 1. Required pack validation

  let requiredPacks: PackCategory[] = [];// = deps.packsByRange[0].categories

  const rngIdx = deps.packsByRange.findIndex(x => x.rangeId === ctx.rangeId);
  if(rngIdx > -1) {
    requiredPacks = deps.packsByRange[rngIdx].categories.slice();
  }


  for (const pcat of requiredPacks) {
    
    if (!ctx.selectedOptionsByPack[pcat.packKey]) {
      errors.push({
        type: "missing_pack",
        packKey: pcat.packKey,
        message: `Missing required selection: ${pcat.packKey}`
      });
    }
  }

  // 2. Option ↔ Variant
  for (const optionId of Object.values(ctx.selectedOptionsByPack)) {
    if (
      deps.incompat.optionVariant.has(
        `${optionId}:${ctx.variantId}`
      )
    ) {
      errors.push({
        type: "option_variant",
        optionId,
        message: `Option is incompatible with selected variant`
      });
    }
  }

  // 3. Option ↔ Option
  const options = Object.values(ctx.selectedOptionsByPack);

  for (let i = 0; i < options.length; i++) {
    for (let j = i + 1; j < options.length; j++) {
      if (
        deps.incompat.optionOption.has(
          `${options[i]}:${options[j]}`
        )
      ) {
        errors.push({
          type: "option_option",
          optionId: options[i],
          message: `Selected options cannot be combined`
        });
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

function buildCurrentPackSelectionContext(): PackSelectionContext {

  const rangeSelect = document.getElementById("productRange")! as HTMLSelectElement;
  const variantSelect = document.getElementById("productVariant")! as HTMLSelectElement;


  const selectedOptionsByPack: Record<string, string> = {};

  document
    .querySelectorAll<HTMLSelectElement>("select[data-pack-key]")
    .forEach(select => {
      if (select.value) {
        selectedOptionsByPack[select.dataset.packKey!] = select.value;
      }
    });

  return {
    rangeId: parseInt(rangeSelect.value, 10),
    variantId: variantSelect.value,
    selectedOptionsByPack
  };
}

function resetClearValidationErrors(){
  const container = document.getElementById("validation-errors")!;
  container.innerHTML = "";
}

function renderValidationErrors(errors: ValidationError[]): void {

  resetClearValidationErrors();
  const container = document.getElementById("validation-errors")!;
  errors.forEach(err => {
    const div = document.createElement("div");
    div.className = "error";
    div.textContent = err.message;
    container.appendChild(div);
  });
}
